import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from '../context/LanguageContext';
import { familyAPI, storiesAPI, voiceAPI, inviteAPI, promptsAPI, billingAPI } from '../services/api';
import { useRecorder } from '../hooks/useRecorder';
import { useAudioCache } from '../hooks/useAudioCache';
import './FamilyCircle.css';

const API_URL = process.env.REACT_APP_API_URL || 'https://api.echomemoir.ai/api';

const FamilyCircle = () => {
  // Inline styles for recording alert and spinner
  const recordingAlertStyles = `
    .recording-alert {
      background: rgba(212, 160, 23, 0.15);
      border: 1px solid rgba(212, 160, 23, 0.3);
      color: #fbbf24;
      padding: 10px 16px;
      border-radius: 8px;
      font-size: 13px;
      line-height: 1.5;
      margin-bottom: 12px;
      animation: fadeInAlert 0.3s ease-out;
    }
    @keyframes fadeInAlert {
      from { opacity: 0; transform: translateY(-8px); }
      to { opacity: 1; transform: translateY(0); }
    }
    .spinner-inline {
      display: inline-block;
      width: 14px;
      height: 14px;
      border: 2px solid rgba(255,255,255,0.3);
      border-top-color: white;
      border-radius: 50%;
      animation: spin 0.8s linear infinite;
      margin-right: 8px;
      vertical-align: middle;
    }
    @keyframes spin {
      to { transform: rotate(360deg); }
    }
    .upload-success-banner {
      display: flex;
      align-items: flex-start;
      gap: 12px;
      background: rgba(34, 197, 94, 0.12);
      border: 1px solid rgba(34, 197, 94, 0.3);
      color: #4ade80;
      padding: 16px;
      border-radius: 12px;
      margin-bottom: 16px;
      animation: fadeInAlert 0.3s ease-out;
    }
    .upload-success-banner .success-icon { font-size: 24px; }
    .upload-success-banner strong { display: block; margin-bottom: 4px; color: #4ade80; }
    .upload-success-banner p { font-size: 13px; color: rgba(74, 222, 128, 0.7); margin: 0; }
  `;
  const { id } = useParams();
  const navigate = useNavigate();
  const { t, language } = useTranslation();
  
  const [circle, setCircle] = useState(null);
  const [stories, setStories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('stories');

  // Search/filter state
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStoryteller, setFilterStoryteller] = useState('');
  const [filterSort, setFilterSort] = useState('newest');
  const [showFilters, setShowFilters] = useState(false);
  const [searchTimeout, setSearchTimeout] = useState(null);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalStories, setTotalStories] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [loadingMore, setLoadingMore] = useState(false);
  const PAGE_SIZE = 20;
  
  // Recording state
  const [storyTitle, setStoryTitle] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState('');
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  
  // Voice profile state
  const [voiceProfiles, setVoiceProfiles] = useState([]);
  const [showVoiceModal, setShowVoiceModal] = useState(false);
  const [voiceName, setVoiceName] = useState('');
  const [voiceSamples, setVoiceSamples] = useState([]);
  const [creatingVoice, setCreatingVoice] = useState(false);

  // Invite state
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('contributor');
  const [inviteRelationship, setInviteRelationship] = useState('');
  const [sendingInvite, setSendingInvite] = useState(false);
  const [inviteSuccess, setInviteSuccess] = useState(false);
  const [inviteError, setInviteError] = useState('');
  const [pendingInvites, setPendingInvites] = useState([]);

  // Member management state
  const [showRoleModal, setShowRoleModal] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);
  const [newRole, setNewRole] = useState('');
  const [updatingRole, setUpdatingRole] = useState(false);
  const [showRemoveModal, setShowRemoveModal] = useState(false);
  const [removingMember, setRemovingMember] = useState(false);

  // Transfer ownership state
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [transferTargetId, setTransferTargetId] = useState('');
  const [transferring, setTransferring] = useState(false);

  // Circle avatar state
  const [circleAvatar, setCircleAvatar] = useState(null);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [memberPhotos, setMemberPhotos] = useState({});

  // Edit circle name state
  const [editingName, setEditingName] = useState(false);
  const [circleName, setCircleName] = useState('');
  const [savingName, setSavingName] = useState(false);
  const [editingDesc, setEditingDesc] = useState(false);
  const [circleDesc, setCircleDesc] = useState('');
  const [savingDesc, setSavingDesc] = useState(false);
  const [showPhotoModal, setShowPhotoModal] = useState(false);
  const [currentUserRole, setCurrentUserRole] = useState(null);

  // Prompts state
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [prompts, setPrompts] = useState([]);
  const [loadingPrompts, setLoadingPrompts] = useState(false);
  const [selectedPrompt, setSelectedPrompt] = useState(null);

  // Family tree state
  const [treeMembers, setTreeMembers] = useState([]);
  const [loadingTree, setLoadingTree] = useState(false);
  const [editingTreeMember, setEditingTreeMember] = useState(null);
  const [showTreeEditModal, setShowTreeEditModal] = useState(false);
  const [showAddTreeModal, setShowAddTreeModal] = useState(false);
  const [viewingMemberStories, setViewingMemberStories] = useState(null);
  const [newTreeMember, setNewTreeMember] = useState({
    name: '', relationship: '', generation: 0, parentId: '', spouseId: '',
    birthYear: '', birthDate: '', birthPlace: '', deathDate: '', deathPlace: '',
    isDeceased: false, bio: ''
  });

  // Export memory book state
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportVersion, setExportVersion] = useState('literary');
  const [exportIncludePhotos, setExportIncludePhotos] = useState(true);
  const [exportStoryteller, setExportStoryteller] = useState('');
  const [exportCategory, setExportCategory] = useState('');
  const [exportGroupByCategory, setExportGroupByCategory] = useState(true);

  // Upgrade prompt state
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [upgradeMessage, setUpgradeMessage] = useState('');
  const [userTier, setUserTier] = useState('free');

  // Guest stories state
  const [guestStories, setGuestStories] = useState([]);
  const [guestStoriesLoading, setGuestStoriesLoading] = useState(false);
  const [guestStoriesFilter, setGuestStoriesFilter] = useState('pending');
  const [guestPendingCount, setGuestPendingCount] = useState(0);
  const [guestStoryAction, setGuestStoryAction] = useState(null);
  const [ownerTier, setOwnerTier] = useState('free');
  const [isCircleVaultKeeper, setIsCircleVaultKeeper] = useState(false);

  // Share token state
  const [shareTokens, setShareTokens] = useState([]);
  const [showShareModal, setShowShareModal] = useState(false);
  const [creatingToken, setCreatingToken] = useState(false);
  const [activeQR, setActiveQR] = useState(null);
  const [expandedTokenId, setExpandedTokenId] = useState(null);
  const [tokenForm, setTokenForm] = useState({
    eventType: 'birthday',
    eventName: '',
    subjectName: '',
    allowVideo: true,
    expiresInDays: 30
  });
  
  const {
    isRecording,
    isPaused,
    duration,
    formattedDuration,
    audioBlob,
    audioUrl,
    startRecording,
    stopRecording,
    pauseRecording,
    resumeRecording,
    resetRecording
  } = useRecorder();

const { saveToCache, loadFromCache, clearCache, hasPending } = useAudioCache();
 
// Pending upload recovery — check on mount + app resume
const [pendingUpload, setPendingUpload] = useState(null);
const [cachedAudioUrl, setCachedAudioUrl] = useState(null);
 
useEffect(() => {
  const checkPending = async () => {
    const meta = await hasPending();
    if (meta) {
      console.log('📱 Found pending audio upload from', new Date(meta.cachedAt).toLocaleTimeString());
      setPendingUpload(meta);
const cached = await loadFromCache();
      if (cached?.blob) {
        // Re-create blob with explicit type for iOS WebView compatibility
        const mimeType = cached.meta?.mimeType || cached.blob.type || 'audio/webm';
        const typedBlob = new Blob([cached.blob], { type: mimeType });
        setCachedAudioUrl(URL.createObjectURL(typedBlob));
      }
    }
  };
  checkPending();

  const handleResume = () => {
    if (!document.hidden) checkPending();
  };
  document.addEventListener('visibilitychange', handleResume);

  return () => {
    document.removeEventListener('visibilitychange', handleResume);
  };
}, []);
 
// Auto-cache audio when recording stops (audioBlob becomes available)
 useEffect(() => {
   if (audioBlob && audioBlob.size > 0) {
     saveToCache(audioBlob, {
       familyCircleId: id,
       title: storyTitle || 'Untitled Story',
     });
   }
 }, [audioBlob]);

  const MAX_RECORDING_SECONDS = 90 * 60;
  const WARN_RECORDING_SECONDS = 85 * 60;

  useEffect(() => {
    loadData();
  }, [id]);

  useEffect(() => {
    if (activeTab === 'guests') {
      loadGuestStories();
      loadShareTokens();
    }
  }, [activeTab]);

  useEffect(() => {
    if (activeTab === 'guests') {
      loadGuestStories(guestStoriesFilter);
    }
  }, [guestStoriesFilter]);


  const loadData = async () => {
    try {
      const [circleRes, storiesRes] = await Promise.all([
        familyAPI.getOne(id),
        storiesAPI.getByFamily(id, { page: 1, limit: PAGE_SIZE })
      ]);
      setCircle(circleRes.data.familyCircle);
      setStories(storiesRes.data.stories);
      setCurrentPage(1);
      setTotalStories(storiesRes.data.pagination?.total || storiesRes.data.stories.length);
      setTotalPages(storiesRes.data.pagination?.pages || 1);

      // Determine current user's role
      const user = JSON.parse(localStorage.getItem('user'));
      if (user && circleRes.data.familyCircle?.members) {
        const member = circleRes.data.familyCircle.members.find(m => 
          (m.userId?._id || m.userId)?.toString() === user._id?.toString()
        );
        setCurrentUserRole(member?.role || null);
      }
      
      // Load voice profiles
      try {
        const voiceRes = await voiceAPI.getProfiles(id);
        setVoiceProfiles(voiceRes.data.voiceProfiles || []);
      } catch (err) {
        console.log('No voice profiles yet');
      }

      // Load pending invites
      try {
        const inviteRes = await inviteAPI.getByFamily(id);
        setPendingInvites(inviteRes.data.invites || []);
      } catch (err) {
        console.log('No pending invites');
      }

      // Load circle avatar
      try {
        const avatarRes = await familyAPI.getAvatar(id);
        setCircleAvatar(avatarRes.data.url);
      } catch (err) {
        // No avatar set
      }

      // Load share tokens on mount so they persist across app restarts
      loadShareTokens();

      // Load user's subscription tier
      try {
        const subRes = await billingAPI.getSubscription();
        setUserTier(subRes.data?.tier || subRes.data?.subscription?.tier || 'free');
      } catch (err) {
        // Default to free if check fails
      }
    } catch (error) {
      console.error('Error loading data:', error);
      if (error.response?.status === 403 || error.response?.status === 404) {
        navigate('/');
      }
    } finally {
      setLoading(false);
    }
  };

  // --- GUEST STORIES ---
  const loadGuestStories = async (status) => {
    const s = status || guestStoriesFilter;
    setGuestStoriesLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(
        `${API_URL}/circles/${id}/guest-stories?status=${s}&limit=50`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const data = await res.json();
      setGuestStories(data.stories || []);
      setGuestPendingCount(data.pending || 0);
      if (data.ownerTier) setOwnerTier(data.ownerTier);
      if (data.isVaultKeeper !== undefined) setIsCircleVaultKeeper(data.isVaultKeeper);
    } catch (err) {
      console.error('loadGuestStories error:', err);
    } finally {
      setGuestStoriesLoading(false);
    }
  };

  const handleGuestStoryApprove = async (storyId) => {
    setGuestStoryAction({ id: storyId, action: 'approving' });
    try {
      const token = localStorage.getItem('token');
      await fetch(`${API_URL}/guest-stories/${storyId}/approve`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}` }
      });
      setGuestStories(prev => prev.filter(s => s._id !== storyId));
      setGuestPendingCount(prev => Math.max(0, prev - 1));
    } catch (err) {
      console.error('approve error:', err);
    } finally {
      setGuestStoryAction(null);
    }
  };

  const handleGuestStoryReject = async (storyId) => {
    setGuestStoryAction({ id: storyId, action: 'rejecting' });
    try {
      const token = localStorage.getItem('token');
      await fetch(`${API_URL}/guest-stories/${storyId}/reject`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}` }
      });
      setGuestStories(prev => prev.filter(s => s._id !== storyId));
      setGuestPendingCount(prev => Math.max(0, prev - 1));
    } catch (err) {
      console.error('reject error:', err);
    } finally {
      setGuestStoryAction(null);
    }
  };

  const handleGuestStoryDelete = async (storyId) => {
    if (!window.confirm('Permanently delete this story and its media? This cannot be undone.')) return;
    setGuestStoryAction({ id: storyId, action: 'deleting' });
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/guest-stories/${storyId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        setGuestStories(prev => prev.filter(s => s._id !== storyId));
      } else {
        const data = await res.json();
        alert(data.error || 'Failed to delete story');
      }
    } catch (err) {
      console.error('delete error:', err);
    } finally {
      setGuestStoryAction(null);
    }
  };

  // --- SHARE TOKENS ---
  const loadShareTokens = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/circles/${id}/share-tokens`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setShareTokens(Array.isArray(data) ? data : data.tokens || []);
    } catch (err) {
      console.error('loadShareTokens error:', err);
    }
  };

  const handleCreateShareToken = async () => {
    if (!tokenForm.eventName.trim()) {
      alert('Please enter an event name');
      return;
    }
    setCreatingToken(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/circles/${id}/share-tokens`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          eventType: tokenForm.eventType,
          eventName: tokenForm.eventName,
          subjectName: tokenForm.subjectName,
          allowVideo: tokenForm.allowVideo,
          expiresInDays: tokenForm.expiresInDays
        })
      });
      const data = await res.json();
      if (data.token) {
        setActiveQR({ token: data.token, shareUrl: data.shareUrl, qrCodePng: data.qrCodePng, eventName: tokenForm.eventName });
        setShowShareModal(false);
        loadShareTokens();
        setTokenForm({ eventType: 'birthday', eventName: '', subjectName: '', allowVideo: true, expiresInDays: 30 });
      } else {
        alert(data.error || 'Failed to create share link');
      }
    } catch (err) {
      console.error('createShareToken error:', err);
      alert('Failed to create share link');
    } finally {
      setCreatingToken(false);
    }
  };

  const handleDeactivateToken = async (tokenId) => {
    if (!window.confirm('Deactivate this share link? People with the QR code won\'t be able to submit new stories.')) return;
    try {
      const token = localStorage.getItem('token');
      await fetch(`${API_URL}/share-tokens/${tokenId}/deactivate`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}` }
      });
      loadShareTokens();
    } catch (err) {
      console.error('deactivate error:', err);
    }
  };


  const loadMoreStories = async () => {
    if (loadingMore || currentPage >= totalPages) return;
    setLoadingMore(true);
    try {
      const nextPage = currentPage + 1;
      const searchParams = {
        page: nextPage,
        limit: PAGE_SIZE,
        search: searchQuery || undefined,
        storytellerId: filterStoryteller || undefined,
        sort: filterSort
      };
      Object.keys(searchParams).forEach(key => {
        if (!searchParams[key]) delete searchParams[key];
      });
      const res = await storiesAPI.getByFamily(id, searchParams);
      setStories(prev => [...prev, ...res.data.stories]);
      setCurrentPage(nextPage);
      setTotalStories(res.data.pagination?.total || totalStories);
      setTotalPages(res.data.pagination?.pages || totalPages);
    } catch (error) {
      console.error('Load more error:', error);
    } finally {
      setLoadingMore(false);
    }
  };

  const searchStories = async (params = {}) => {
    try {
      const searchParams = {
        search: params.search ?? searchQuery,
        storytellerId: params.storytellerId ?? filterStoryteller,
        sort: params.sort ?? filterSort
      };
      
      // Remove empty params
      Object.keys(searchParams).forEach(key => {
        if (!searchParams[key]) delete searchParams[key];
      });

      const res = await storiesAPI.getByFamily(id, { ...searchParams, page: 1, limit: PAGE_SIZE });
      setStories(res.data.stories);
      setCurrentPage(1);
      setTotalStories(res.data.pagination?.total || res.data.stories.length);
      setTotalPages(res.data.pagination?.pages || 1);
    } catch (error) {
      console.error('Search error:', error);
    }
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    
    // Debounce search
    if (searchTimeout) clearTimeout(searchTimeout);
    setSearchTimeout(setTimeout(() => {
      searchStories({ search: value });
    }, 300));
  };

  const handleFilterChange = (type, value) => {
    if (type === 'storyteller') {
      setFilterStoryteller(value);
      searchStories({ storytellerId: value });
    } else if (type === 'sort') {
      setFilterSort(value);
      searchStories({ sort: value });
    }
  };

  const clearFilters = () => {
    setSearchQuery('');
    setFilterStoryteller('');
    setFilterSort('newest');
    setShowFilters(false);
    searchStories({ search: '', storytellerId: '', sort: 'newest' });
  };

  const hasActiveFilters = searchQuery || filterStoryteller || filterSort !== 'newest';

  const handleStartRecording = async () => {
    try {
      await startRecording();
    } catch (error) {
      alert('Could not access microphone. Please check permissions.');
    }
  };

  // Auto-stop at 90 min, warn at 85 min
  useEffect(() => {
    if (!isRecording || isPaused) return;
    if (duration >= MAX_RECORDING_SECONDS) {
      stopRecording();
      alert('Recording stopped — 90 minute limit reached. Your recording has been saved.');
    }
  }, [duration, isRecording, isPaused]);

  // Show alert when recording begins
  const [showRecordingAlert, setShowRecordingAlert] = useState(false);
  useEffect(() => {
    if (isRecording && !isPaused) {
      setShowRecordingAlert(true);
      const timer = setTimeout(() => setShowRecordingAlert(false), 6000);
      return () => clearTimeout(timer);
    }
  }, [isRecording]);

const handleUpload = async (retryFromCache = false) => {
  let blobToUpload = audioBlob;
  let titleToUse = storyTitle || 'Untitled Story';

  // If retrying from cache (e.g. after phone sleep killed the upload)
  if (retryFromCache) {
    const cached = await loadFromCache();
    if (cached && cached.blob) {
      blobToUpload = cached.blob;
      titleToUse = cached.meta.title || titleToUse;
      console.log('📂 Retrying upload from cache');
    } else {
      alert('No recording found. Please record again.');
      return;
    }
  }

  if (!blobToUpload) {
    alert('No recording found. Please record again.');
    return;
  }

  setUploading(true);
  setUploadProgress('Uploading...');
 
  try {
    // Derive correct file extension from actual blob type
    const mimeType = blobToUpload.type || 'audio/webm';
    const ext = mimeType.includes('mp4') ? 'mp4'
              : mimeType.includes('ogg') ? 'ogg'
              : mimeType.includes('wav') ? 'wav'
              : 'webm';
    const filename = `recording.${ext}`;
 
    const formData = new FormData();
    formData.append('audio', blobToUpload, filename);
    formData.append('familyCircleId', id);
    formData.append('title', titleToUse);
 
    setUploadProgress('Uploading audio...');
    const res = await storiesAPI.quickUpload(formData);
 
    // Upload succeeded — clear the cache
    await clearCache();
    setPendingUpload(null);

    if (cachedAudioUrl) URL.revokeObjectURL(cachedAudioUrl);
    setCachedAudioUrl(null);
 
    setUploadProgress('Transcribing...');
 
    // Poll for completion
    const storyId = res.data.story._id;
    let attempts = 0;
    const maxAttempts = 60;
 
    const checkStatus = async () => {
      try {
        const statusRes = await storiesAPI.getStatus(storyId);
        if (statusRes.data.status === 'complete') {
          setUploadProgress('Complete!');
          const storiesRes = await storiesAPI.getByFamily(id, { page: 1, limit: PAGE_SIZE });
          setStories(storiesRes.data.stories);
          setCurrentPage(1);
          setTotalStories(storiesRes.data.pagination?.total || storiesRes.data.stories.length);
          setTotalPages(storiesRes.data.pagination?.pages || 1);
          resetRecording();
          setStoryTitle('');
          setUploading(false);
          setUploadProgress('');
          setShowSuccessMessage(true);
          setTimeout(() => {
            setShowSuccessMessage(false);
            setActiveTab('stories');
          }, 4000);
        } else if (statusRes.data.status === 'failed') {
          setUploading(false);
          setUploadProgress('');
          alert('Transcription failed. Please try again.');
        } else if (attempts < maxAttempts) {
          attempts++;
          setTimeout(checkStatus, 2000);
        } else {
          setUploading(false);
          setUploadProgress('');
          alert('Transcription timed out. Please try again.');
        }
      } catch (pollError) {
        console.error('Status poll error:', pollError);
        setUploading(false);
        setUploadProgress('');
        // Don't clear cache on poll error — upload may have succeeded
        alert('Could not check status. Your story may still be processing — check back in a moment.');
      }
    };
 
    setTimeout(checkStatus, 2000);
 
  } catch (error) {
    console.error('Upload error:', error);
    setUploadProgress('');
    setUploading(false);
 
    const status = error.response?.status;
    const data = error.response?.data;
 
    if (status === 403 && (data?.upgrade || data?.error?.includes('limit'))) {
      setUpgradeMessage(data.message || data.error || 'You have reached your plan limit. Upgrade to continue recording stories!');
      setShowUpgradeModal(true);
      // Clear cache since this isn't a transient error
      await clearCache();
      setPendingUpload(null);
    } else {
      // Transient error (network, timeout, phone slept) — audio is safe in cache
      alert('Upload failed — your recording is saved on this device. Tap "Retry Upload" to try again.');
      setPendingUpload({ familyCircleId: id, title: titleToUse, cachedAt: Date.now() });
    }
  }
};

  const handleCancelRecording = () => {
    resetRecording();
    setStoryTitle('');
  };

  const handleExportMemoryBook = async () => {
    // Check plan limits before opening PDF
    try {
      const subRes = await billingAPI.getSubscription();
      const tier = subRes.data?.tier || subRes.data?.subscription?.tier || 'free';
      const pdfExports = subRes.data?.limits?.pdfExports;
      if (tier === 'free' || pdfExports === false) {
        setUpgradeMessage('PDF exports are available on Legacy Keeper and Legacy Vault plans.');
        setShowUpgradeModal(true);
        setShowExportModal(false);
        return;
      }
    } catch (e) {
      // If check fails, proceed anyway and let server handle it
    }

    const token = localStorage.getItem('token');
    const params = new URLSearchParams({
      version: exportVersion,
      includePhotos: exportIncludePhotos.toString(),
      groupByCategory: exportGroupByCategory.toString(),
      token
    });
    if (exportStoryteller) params.set('storytellerId', exportStoryteller);
    if (exportCategory) params.set('category', exportCategory);

    const url = `${API_URL}/stories/family/${id}/export/pdf?${params.toString()}`;
    window.open(url, '_blank');
    setShowExportModal(false);
  };

  // Get unique categories from stories for export filter
  const storyCategories = [...new Set(stories.map(s => s.category).filter(Boolean))];

  const handleSendInvite = async (e) => {
    e.preventDefault();
    setInviteError('');
    
    if (!inviteEmail.trim()) {
      setInviteError('Please enter an email address');
      return;
    }

    setSendingInvite(true);
    try {
      const res = await inviteAPI.send({
        familyCircleId: id,
        email: inviteEmail,
        role: inviteRole,
        relationship: inviteRelationship
      });
      
      setPendingInvites([res.data.invite, ...pendingInvites]);
      setInviteSuccess(true);
    } catch (error) {
      console.error('Send invite error:', error);
      if (error.response?.status === 403) {
        setInviteError('Only Vault Keepers can invite new members.');
      } else {
        setInviteError(error.response?.data?.error || 'Failed to send invite');
      }
    } finally {
      setSendingInvite(false);
    }
  };

  const handleAvatarUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadingAvatar(true);
    try {
      const formData = new FormData();
      formData.append('avatar', file);
      const res = await familyAPI.uploadAvatar(id, formData);
      setCircleAvatar(res.data.avatarUrl);
    } catch (error) {
      console.error('Avatar upload error:', error);
      if (error.response?.status === 403) {
        alert('Only Vault Keepers can change the circle avatar.');
      } else {
        alert('Failed to upload image');
      }
    } finally {
      setUploadingAvatar(false);
    }
  };

  const handleSaveName = async () => {
    if (!circleName.trim() || circleName.trim() === circle?.name) {
      setEditingName(false);
      return;
    }
    setSavingName(true);
    try {
      const res = await familyAPI.update(id, { name: circleName.trim() });
      setCircle(res.data.familyCircle);
      setEditingName(false);
    } catch (error) {
      console.error('Update name error:', error);
    } finally {
      setSavingName(false);
    }
  };

  const handleSaveDesc = async () => {
    if (circleDesc.trim() === (circle?.description || '')) {
      setEditingDesc(false);
      return;
    }
    setSavingDesc(true);
    try {
      const res = await familyAPI.update(id, { description: circleDesc.trim() });
      setCircle(res.data.familyCircle);
      setEditingDesc(false);
    } catch (error) {
      console.error('Update description error:', error);
    } finally {
      setSavingDesc(false);
    }
  };

  const handleRemoveAvatar = async () => {
    if (!window.confirm('Remove the family circle photo?')) return;
    try {
      const res = await familyAPI.update(id, { avatar: null });
      setCircleAvatar(null);
      setCircle(res.data.familyCircle);
    } catch (error) {
      console.error('Remove avatar error:', error);
    }
  };

  const handleCloseInviteModal = () => {
    setShowInviteModal(false);
    setInviteEmail('');
    setInviteRole('contributor');
    setInviteRelationship('');
    setInviteSuccess(false);
    setInviteError('');
  };

  const handleCancelInvite = async (inviteId) => {
    if (!window.confirm('Cancel this invite?')) return;
    
    try {
      await inviteAPI.cancel(inviteId);
      setPendingInvites(pendingInvites.filter(i => i._id !== inviteId));
    } catch (error) {
      console.error('Cancel invite error:', error);
      if (error.response?.status === 403) {
        alert('Only Vault Keepers can cancel invites.');
      } else {
        alert('Failed to cancel invite');
      }
    }
  };

  const handleChangeRole = async () => {
    if (!selectedMember || !newRole) return;
    
    setUpdatingRole(true);
    try {
      await familyAPI.changeMemberRole(id, selectedMember.userId?._id || selectedMember.userId, newRole);
      
      // Update local state
      setCircle(prev => ({
        ...prev,
        members: prev.members.map(m => 
          (m.userId?._id || m.userId) === (selectedMember.userId?._id || selectedMember.userId)
            ? { ...m, role: newRole }
            : m
        )
      }));
      
      setShowRoleModal(false);
      setSelectedMember(null);
      setNewRole('');
    } catch (error) {
      console.error('Change role error:', error);
      if (error.response?.status === 403) {
        alert('Only Vault Keepers can change member roles.');
      } else {
        alert(error.response?.data?.error || 'Failed to change role');
      }
    } finally {
      setUpdatingRole(false);
    }
  };

  const handleRemoveMember = async () => {
    if (!selectedMember) return;
    
    setRemovingMember(true);
    try {
      await familyAPI.removeMember(id, selectedMember.userId?._id || selectedMember.userId);
      
      // Update local state
      setCircle(prev => ({
        ...prev,
        members: prev.members.filter(m => 
          (m.userId?._id || m.userId) !== (selectedMember.userId?._id || selectedMember.userId)
        )
      }));
      
      setShowRemoveModal(false);
      setSelectedMember(null);
    } catch (error) {
      console.error('Remove member error:', error);
      if (error.response?.status === 403) {
        alert('Only Vault Keepers can remove members from the circle.');
      } else {
        alert(error.response?.data?.error || 'Failed to remove member');
      }
    } finally {
      setRemovingMember(false);
    }
  };

  const openRoleModal = (member) => {
    setSelectedMember(member);
    setNewRole(member.role);
    setShowRoleModal(true);
  };

  const openRemoveModal = (member) => {
    setSelectedMember(member);
    setShowRemoveModal(true);
  };

  const handleTransferOwnership = async () => {
    if (!transferTargetId) return;
    setTransferring(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/family/${id}/transfer-ownership`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ newOwnerId: transferTargetId })
      });
      const data = await res.json();
      if (!res.ok) {
        alert(data.error || 'Failed to transfer ownership');
        return;
      }
      setCircle(data.familyCircle);
      setShowTransferModal(false);
      setTransferTargetId('');
      alert('✅ Ownership transferred successfully. You remain a Vault Keeper.');
    } catch (error) {
      console.error('Transfer ownership error:', error);
      alert('Failed to transfer ownership');
    } finally {
      setTransferring(false);
    }
  };

  const isOwner = (member) => {
    return circle?.ownerId === (member.userId?._id || member.userId);
  };

  const getRoleLabel = (role) => {
    const labels = {
      vaultKeeper: '👑 Vault Keeper',
      storyteller: '📖 Storyteller',
      contributor: '✍️ Contributor',
      viewer: '👁️ Viewer'
    };
    return labels[role] || role;
  };

  // Prompts functions
  const loadCategories = async () => {
    try {
      const res = await promptsAPI.getCategories(language);
      setCategories(res.data.categories || []);
    } catch (error) {
      console.error('Load categories error:', error);
    }
  };

  const loadPromptsByCategory = async (category) => {
    setLoadingPrompts(true);
    setSelectedCategory(category);
    try {
      const res = await promptsAPI.getAll({ category: category.id, familyCircleId: id, lang: language });
      setPrompts(res.data.prompts || []);
    } catch (error) {
      console.error('Load prompts error:', error);
    } finally {
      setLoadingPrompts(false);
    }
  };

  const handleSelectPrompt = async (prompt) => {
    setSelectedPrompt(prompt);
    setStoryTitle(prompt.text);
    setActiveTab('record');
    
    // Track usage
    try {
      await promptsAPI.trackUsage(prompt._id);
    } catch (error) {
      console.log('Track usage failed:', error);
    }
  };

  const handleBackToCategories = () => {
    setSelectedCategory(null);
    setPrompts([]);
  };

  // Load categories when switching to prompts tab or language changes
  useEffect(() => {
    if (activeTab === 'prompts') {
      loadCategories();
    }
  }, [activeTab, language]);

  // Load tree when switching to tree tab
  useEffect(() => {
    if (activeTab === 'tree' && treeMembers.length === 0) {
      loadFamilyTree();
    }
  }, [activeTab]);

  const loadFamilyTree = async () => {
    setLoadingTree(true);
    try {
      const res = await familyAPI.getTree(id);
      setTreeMembers(res.data.members || []);
    } catch (error) {
      console.error('Load tree error:', error);
    } finally {
      setLoadingTree(false);
    }
  };

  const handleEditTreeMember = (member) => {
    setEditingTreeMember({
      ...member,
      parentId: member.parentId || '',
      spouseId: member.spouseId || '',
      generation: member.generation || 0,
      birthYear: member.birthYear || '',
      relationship: member.relationship || '',
      birthDate: member.birthDate || '',
      birthPlace: member.birthPlace || '',
      deathDate: member.deathDate || '',
      deathPlace: member.deathPlace || '',
      isDeceased: member.isDeceased || false,
      bio: member.bio || ''
    });
    setShowTreeEditModal(true);
  };

  const handleSaveTreeMember = async () => {
    if (!editingTreeMember) return;
    
    try {
      // Use different API for tree-only vs regular members
      if (editingTreeMember.isTreeOnly) {
        await familyAPI.updateTreeOnlyMember(id, editingTreeMember.id, {
          name: editingTreeMember.name,
          parentId: editingTreeMember.parentId || null,
          spouseId: editingTreeMember.spouseId || null,
          generation: parseInt(editingTreeMember.generation) || 0,
          birthYear: editingTreeMember.birthYear ? parseInt(editingTreeMember.birthYear) : null,
          relationship: editingTreeMember.relationship,
          birthDate: editingTreeMember.birthDate || null,
          birthPlace: editingTreeMember.birthPlace || null,
          deathDate: editingTreeMember.deathDate || null,
          deathPlace: editingTreeMember.deathPlace || null,
          isDeceased: editingTreeMember.isDeceased || false,
          bio: editingTreeMember.bio || null
        });
      } else {
        await familyAPI.updateTreeMember(id, editingTreeMember.id, {
          parentId: editingTreeMember.parentId || null,
          spouseId: editingTreeMember.spouseId || null,
          generation: parseInt(editingTreeMember.generation) || 0,
          birthYear: editingTreeMember.birthYear ? parseInt(editingTreeMember.birthYear) : null,
          relationship: editingTreeMember.relationship,
          birthDate: editingTreeMember.birthDate || null,
          birthPlace: editingTreeMember.birthPlace || null,
          deathDate: editingTreeMember.deathDate || null,
          deathPlace: editingTreeMember.deathPlace || null,
          isDeceased: editingTreeMember.isDeceased || false,
          bio: editingTreeMember.bio || null
        });
      }
      
      // Reload full tree
      try {
        const res = await familyAPI.getTree(id);
        setTreeMembers(res.data.members || []);
      } catch (e) {}
      
      setShowTreeEditModal(false);
      setEditingTreeMember(null);
    } catch (error) {
      console.error('Save tree member error:', error);
      if (error.response?.status === 403) {
        alert('Only Storytellers and Vault Keepers can edit the family tree.');
      } else {
        alert(error.response?.data?.error || 'Failed to save');
      }
    }
  };

  const handleAddTreeMember = async () => {
    if (!newTreeMember.name.trim()) {
      alert('Name is required');
      return;
    }
    try {
      await familyAPI.addTreeMember(id, {
        ...newTreeMember,
        generation: parseInt(newTreeMember.generation) || 0,
        birthYear: newTreeMember.birthYear ? parseInt(newTreeMember.birthYear) : null
      });
      
      // Reload tree
      const res = await familyAPI.getTree(id);
      setTreeMembers(res.data.members || []);
      
      setShowAddTreeModal(false);
      setNewTreeMember({
        name: '', relationship: '', generation: 0, parentId: '', spouseId: '',
        birthYear: '', birthDate: '', birthPlace: '', deathDate: '', deathPlace: '',
        isDeceased: false, bio: ''
      });
    } catch (error) {
      console.error('Add tree member error:', error);
      if (error.response?.status === 403) {
        alert('Only Storytellers and Vault Keepers can add to the family tree.');
      } else {
        alert(error.response?.data?.error || 'Failed to add');
      }
    }
  };

  const handleDeleteTreeMember = async (memberId) => {
    if (!window.confirm('Remove this person from the family tree?')) return;
    try {
      await familyAPI.deleteTreeOnlyMember(id, memberId);
      const res = await familyAPI.getTree(id);
      setTreeMembers(res.data.members || []);
      setShowTreeEditModal(false);
      setEditingTreeMember(null);
    } catch (error) {
      console.error('Delete tree member error:', error);
      if (error.response?.status === 403) {
        alert('Only Storytellers and Vault Keepers can remove family tree members.');
      } else {
        alert(error.response?.data?.error || 'Failed to remove');
      }
    }
  };

  const getGenerationLabel = (gen) => {
    const labels = {
      '-3': 'Great-Grandparents',
      '-2': 'Grandparents',
      '-1': 'Parents',
      '0': 'You & Siblings',
      '1': 'Children',
      '2': 'Grandchildren',
      '3': 'Great-Grandchildren'
    };
    return labels[gen.toString()] || `Generation ${gen}`;
  };

  const groupByGeneration = (members) => {
    const grouped = {};
    members.forEach(m => {
      const gen = m.generation || 0;
      if (!grouped[gen]) grouped[gen] = [];
      grouped[gen].push(m);
    });
    return Object.entries(grouped)
      .sort(([a], [b]) => parseInt(a) - parseInt(b))
      .map(([gen, members]) => ({ generation: parseInt(gen), members }));
  };

  const handleVoiceSampleChange = (e) => {
    const files = Array.from(e.target.files);
    setVoiceSamples(files);
  };

  const handleCreateVoice = async (e) => {
    e.preventDefault();
    
    if (!voiceName.trim()) {
      alert('Please enter a name for the voice profile');
      return;
    }
    
    if (voiceSamples.length === 0) {
      alert('Please upload at least one audio sample');
      return;
    }

    setCreatingVoice(true);
    try {
      const formData = new FormData();
      formData.append('name', voiceName);
      formData.append('familyCircleId', id);
      voiceSamples.forEach((file) => {
        formData.append('samples', file);
      });

      const res = await voiceAPI.createClone(formData);
      setVoiceProfiles([...voiceProfiles, res.data.voiceProfile]);
      setShowVoiceModal(false);
      setVoiceName('');
      setVoiceSamples([]);
      alert('Voice profile created successfully!');
    } catch (error) {
      console.error('Voice creation error:', error);
      if (error.response?.status === 403 && error.response?.data?.upgrade) {
        setShowVoiceModal(false);
        setUpgradeMessage(error.response.data.message);
        setShowUpgradeModal(true);
      } else {
        alert(error.response?.data?.details || 'Failed to create voice profile');
      }
    } finally {
      setCreatingVoice(false);
    }
  };

  if (loading) {
    return (
      <div className="family-loading">
        <div className="spinner-large"></div>
        <p>{t('loading')}</p>
      </div>
    );
  }

  return (
    <div className="family-page">
      <style>{recordingAlertStyles}</style>
      <header className="family-header">
        <button className="btn-back" onClick={() => navigate('/')}>
          ← {t('back')}
        </button>
        <div className="header-info">
          <div className="circle-header-row">
            <div className="circle-avatar-wrapper">
              {circleAvatar ? (
                <div className="circle-avatar-container" onClick={() => setShowPhotoModal(true)}>
                  <img 
                    src={circleAvatar} 
                    alt={circle?.name} 
                    className="circle-avatar"
                    style={{ cursor: 'pointer' }}
                    title="Click to enlarge"
                  />
                </div>
              ) : (
                <div className="circle-avatar-placeholder circle-initials">
                  {circle?.name ? circle.name.split(' ').map(w => w[0]).join('').substring(0, 2).toUpperCase() : '?'}
                </div>
              )}
              {currentUserRole === 'vaultKeeper' && (
                <label className="avatar-upload-btn" title="Change family image">
                  📷
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleAvatarUpload}
                    hidden
                  />
                </label>
              )}
              {uploadingAvatar && <div className="avatar-spinner"><span className="spinner"></span></div>}
            </div>
            <div className="circle-header-text">
              {editingName ? (
                <div className="name-edit">
                  <input
                    type="text"
                    value={circleName}
                    onChange={(e) => setCircleName(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleSaveName();
                      if (e.key === 'Escape') setEditingName(false);
                    }}
                    autoFocus
                    className="name-edit-input"
                  />
                  <button className="btn-name-save" onClick={handleSaveName} disabled={savingName}>
                    {savingName ? '...' : '✓'}
                  </button>
                  <button className="btn-name-cancel" onClick={() => setEditingName(false)}>✕</button>
                </div>
              ) : (
                <h1 
                  onClick={() => { if (currentUserRole === 'vaultKeeper') { setCircleName(circle?.name || ''); setEditingName(true); }}}
                  className={currentUserRole === 'vaultKeeper' ? 'editable-name' : ''}
                  title={currentUserRole === 'vaultKeeper' ? 'Click to edit name' : ''}
                >
                  {circle?.name} {currentUserRole === 'vaultKeeper' && <span className="edit-hint">✏️</span>}
                </h1>
              )}
              {editingDesc ? (
                <div className="desc-edit">
                  <textarea
                    value={circleDesc}
                    onChange={(e) => setCircleDesc(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSaveDesc(); }
                      if (e.key === 'Escape') setEditingDesc(false);
                    }}
                    autoFocus
                    className="desc-edit-input"
                    placeholder={t('addDescription') || 'Add a description...'}
                    rows={2}
                  />
                  <div className="desc-edit-actions">
                    <button className="btn-name-save" onClick={handleSaveDesc} disabled={savingDesc}>
                      {savingDesc ? '...' : '✓'}
                    </button>
                    <button className="btn-name-cancel" onClick={() => setEditingDesc(false)}>✕</button>
                  </div>
                </div>
              ) : (
                <>
                  {circle?.description ? (
                    <p 
                      className={`circle-description ${currentUserRole === 'vaultKeeper' ? 'editable-desc' : ''}`}
                      onClick={() => { if (currentUserRole === 'vaultKeeper') { setCircleDesc(circle?.description || ''); setEditingDesc(true); }}}
                      title={currentUserRole === 'vaultKeeper' ? 'Click to edit description' : ''}
                    >
                      {circle.description} {currentUserRole === 'vaultKeeper' && <span className="edit-hint">✏️</span>}
                    </p>
                  ) : currentUserRole === 'vaultKeeper' ? (
                    <p 
                      className="circle-description add-desc"
                      onClick={() => { setCircleDesc(''); setEditingDesc(true); }}
                    >
                      + {t('addDescription') || 'Add a description'}
                    </p>
                  ) : null}
                </>
              )}
              <p>{circle?.members?.length || 1} {t('members')}</p>
            </div>
          </div>
        </div>
      </header>

      <div className="family-tabs">
        <button 
          className={`tab ${activeTab === 'stories' ? 'active' : ''}`}
          onClick={() => setActiveTab('stories')}
        >
          📚 {t('stories')}
        </button>
        {currentUserRole !== 'viewer' && (
        <button 
          className={`tab ${activeTab === 'prompts' ? 'active' : ''}`}
          onClick={() => setActiveTab('prompts')}
        >
          💡 {t('prompts')}
        </button>
        )}
        {currentUserRole !== 'viewer' && (
        <button 
          className={`tab ${activeTab === 'record' ? 'active' : ''}`}
          onClick={() => setActiveTab('record')}
        >
          🎙️ {t('record')}
        </button>
        )}
        <button 
          className={`tab ${activeTab === 'tree' ? 'active' : ''}`}
          onClick={() => setActiveTab('tree')}
        >
          🌳 {t('tree')}
        </button>
        <button 
          className={`tab ${activeTab === 'members' ? 'active' : ''}`}
          onClick={() => setActiveTab('members')}
        >
          👥 {t('membersTab')}
        </button>
        {currentUserRole && (
          <button
            className={`tab ${activeTab === 'guests' ? 'active' : ''}`}
            onClick={() => setActiveTab('guests')}
            style={{ position: 'relative' }}
          >
            💌 Memories
            {currentUserRole === 'vaultKeeper' && guestPendingCount > 0 && (
              <span style={{
                position: 'absolute', top: '-6px', right: '-6px',
                background: '#ef4444', color: 'white',
                fontSize: '10px', fontWeight: '700',
                padding: '2px 6px', borderRadius: '100px',
                minWidth: '18px', textAlign: 'center'
              }}>
                {guestPendingCount}
              </span>
            )}
          </button>
        )}
      </div>

      <main className="family-main">
        {/* Guest Stories Tab */}
        {activeTab === 'guests' && (
          <div className="stories-tab" style={{ padding: '24px 32px' }}>

            {/* Header + Generate Link + Export buttons */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px' }}>
              <div>
                <h2 style={{ fontSize: '20px', fontWeight: '600', color: '#fff', marginBottom: '4px' }}>
                  💌 Shared Memories
                </h2>
                <p style={{ color: 'rgba(255,255,255,0.45)', fontSize: '13px' }}>
                  Stories submitted via your QR share links
                </p>
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                {guestStories.some(s => s.status === 'approved') && (
                  <button
                    onClick={() => {
                      const authToken = localStorage.getItem('token');
                      const url = `${API_URL}/circles/${id}/guest-stories/export/pdf?token=${authToken}`;
                      window.open(url, '_blank');
                    }}
                    style={{
                      padding: '10px 18px', borderRadius: '10px', cursor: 'pointer',
                      background: 'rgba(255,255,255,0.04)',
                      border: '1px solid rgba(255,255,255,0.12)',
                      color: 'rgba(255,255,255,0.6)', fontSize: '13px', fontWeight: '500',
                      display: 'flex', alignItems: 'center', gap: '6px', whiteSpace: 'nowrap'
                    }}
                  >
                    📄 Export PDF
                  </button>
                )}
                {isCircleVaultKeeper && (
                <button
                  onClick={async () => {
                    try {
                      const subRes = await billingAPI.getSubscription();
                      const tier = subRes.data?.tier || subRes.data?.subscription?.tier || 'free';
                      if (tier === 'free') {
                        setUpgradeMessage('Share links & QR codes are available on Legacy Keeper and Legacy Vault plans. Upgrade to create shareable memory links for events.');
                        setShowUpgradeModal(true);
                        return;
                      }
                    } catch (e) {
                      // If check fails, proceed and let server handle it
                    }
                    setShowShareModal(true);
                  }}
                  style={{
                    padding: '10px 18px', borderRadius: '10px', border: 'none', cursor: 'pointer',
                    background: 'linear-gradient(135deg, rgba(139,92,246,0.3), rgba(139,92,246,0.15))',
                    border: '1px solid rgba(139,92,246,0.4)',
                    color: '#a78bfa', fontSize: '13px', fontWeight: '600',
                    display: 'flex', alignItems: 'center', gap: '6px', whiteSpace: 'nowrap'
                  }}
                >
                  🔗 Generate Share Link
                </button>
                )}
              </div>
            </div>

            {/* Active QR display — vaultKeeper only */}
            {isCircleVaultKeeper && activeQR && (
              <div style={{
                background: 'rgba(139,92,246,0.08)', border: '1px solid rgba(139,92,246,0.2)',
                borderRadius: '14px', padding: '20px', marginBottom: '20px',
                display: 'flex', gap: '20px', alignItems: 'center', flexWrap: 'wrap'
              }}>
                <img src={activeQR.qrCodePng} alt="QR Code" style={{ width: '120px', height: '120px', borderRadius: '8px', background: '#fff', padding: '6px' }} />
                <div style={{ flex: 1, minWidth: '200px' }}>
                  <p style={{ color: '#a78bfa', fontWeight: '600', fontSize: '14px', marginBottom: '6px' }}>
                    ✅ Share link ready — {activeQR.eventName}
                  </p>
                  <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '12px', marginBottom: '12px', wordBreak: 'break-all' }}>
                    {activeQR.shareUrl}
                  </p>
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    <button
                      onClick={() => navigator.clipboard.writeText(activeQR.shareUrl).then(() => alert('Link copied!'))}
                      style={{ padding: '7px 14px', borderRadius: '8px', border: '1px solid rgba(139,92,246,0.3)', background: 'transparent', color: '#a78bfa', fontSize: '12px', cursor: 'pointer' }}
                    >
                      📋 Copy Link
                    </button>
                    <button
                      onClick={() => {
                        const a = document.createElement('a');
                        a.href = activeQR.qrCodePng;
                        a.download = `echomemoir-qr-${activeQR.eventName.replace(/\s+/g,'-')}.png`;
                        a.click();
                      }}
                      style={{ padding: '7px 14px', borderRadius: '8px', border: '1px solid rgba(139,92,246,0.3)', background: 'transparent', color: '#a78bfa', fontSize: '12px', cursor: 'pointer' }}
                    >
                      ⬇️ Save QR
                    </button>
                    <button
                      onClick={() => setActiveQR(null)}
                      style={{ padding: '7px 14px', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.1)', background: 'transparent', color: 'rgba(255,255,255,0.4)', fontSize: '12px', cursor: 'pointer' }}
                    >
                      ✕ Dismiss
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Share links — vaultKeeper only */}
            {isCircleVaultKeeper && shareTokens.length > 0 && (
              <div style={{ marginBottom: '20px' }}>
                <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: '12px', marginBottom: '10px', fontWeight: '500', letterSpacing: '0.05em', textTransform: 'uppercase' }}>
                  Active Share Links
                </p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {shareTokens.filter(t => t.isActive).map(st => {
                    const isExpanded = expandedTokenId === st._id;
                    const shareUrl = `https://api.echomemoir.ai/share/${st.token}`;
                    return (
                      <div key={st._id} style={{ background: isExpanded ? 'rgba(139,92,246,0.06)' : 'rgba(255,255,255,0.03)', border: `1px solid ${isExpanded ? 'rgba(139,92,246,0.2)' : 'rgba(255,255,255,0.07)'}`, borderRadius: '10px', padding: '10px 14px', transition: 'all 0.2s ease' }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <div
                            style={{ cursor: 'pointer', flex: 1 }}
                            onClick={() => setExpandedTokenId(isExpanded ? null : st._id)}
                          >
                            <p style={{ color: '#fff', fontSize: '13px', fontWeight: '500', margin: 0 }}>
                              {isExpanded ? '▾' : '▸'} {st.eventName}
                            </p>
                            <p style={{ color: 'rgba(255,255,255,0.35)', fontSize: '11px', margin: 0, paddingLeft: '16px' }}>
                              {st.eventType} · {st.pendingCount || 0} pending · expires {new Date(st.expiresAt).toLocaleDateString()}
                            </p>
                          </div>
                          <div style={{ display: 'flex', gap: '6px' }}>
                            <button
                              onClick={() => navigator.clipboard.writeText(shareUrl).then(() => alert('Link copied!'))}
                              style={{ padding: '5px 10px', borderRadius: '7px', border: '1px solid rgba(139,92,246,0.3)', background: 'transparent', color: '#a78bfa', fontSize: '11px', cursor: 'pointer' }}
                            >
                              📋
                            </button>
                            <button
                              onClick={() => handleDeactivateToken(st._id)}
                              style={{ padding: '5px 10px', borderRadius: '7px', border: '1px solid rgba(239,68,68,0.2)', background: 'transparent', color: '#ef4444', fontSize: '11px', cursor: 'pointer' }}
                            >
                              Deactivate
                            </button>
                          </div>
                        </div>
                        {isExpanded && (
                          <div style={{
                            marginTop: '14px', paddingTop: '14px',
                            borderTop: '1px solid rgba(255,255,255,0.06)',
                            display: 'flex', gap: '16px', alignItems: 'center', flexWrap: 'wrap'
                          }}>
                            {st.qrCodePng && (
                              <img src={st.qrCodePng} alt="QR Code" style={{ width: '100px', height: '100px', borderRadius: '8px', background: '#fff', padding: '5px' }} />
                            )}
                            <div style={{ flex: 1, minWidth: '180px' }}>
                              <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '12px', marginBottom: '10px', wordBreak: 'break-all' }}>
                                {shareUrl}
                              </p>
                              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                                <button
                                  onClick={() => navigator.clipboard.writeText(shareUrl).then(() => alert('Link copied!'))}
                                  style={{ padding: '6px 12px', borderRadius: '8px', border: '1px solid rgba(139,92,246,0.3)', background: 'transparent', color: '#a78bfa', fontSize: '12px', cursor: 'pointer' }}
                                >
                                  📋 Copy Link
                                </button>
                                {st.qrCodePng && (
                                  <button
                                    onClick={() => {
                                      const a = document.createElement('a');
                                      a.href = st.qrCodePng;
                                      a.download = `echomemoir-qr-${st.eventName.replace(/\s+/g,'-')}.png`;
                                      a.click();
                                    }}
                                    style={{ padding: '6px 12px', borderRadius: '8px', border: '1px solid rgba(139,92,246,0.3)', background: 'transparent', color: '#a78bfa', fontSize: '12px', cursor: 'pointer' }}
                                  >
                                    ⬇️ Save QR
                                  </button>
                                )}
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Filter tabs — vaultKeeper only */}
            {isCircleVaultKeeper && (
            <div style={{ display: 'flex', gap: '8px', marginBottom: '20px', flexWrap: 'wrap' }}>
              {['pending', 'approved', 'rejected', 'all'].map(f => (
                <button
                  key={f}
                  onClick={() => setGuestStoriesFilter(f)}
                  style={{
                    padding: '7px 16px', borderRadius: '10px', border: '1px solid',
                    borderColor: guestStoriesFilter === f ? 'rgba(139,92,246,0.5)' : 'rgba(255,255,255,0.1)',
                    background: guestStoriesFilter === f ? 'rgba(139,92,246,0.15)' : 'transparent',
                    color: guestStoriesFilter === f ? '#a78bfa' : 'rgba(255,255,255,0.5)',
                    fontSize: '13px', fontWeight: '500', cursor: 'pointer',
                    display: 'flex', alignItems: 'center', gap: '6px'
                  }}
                >
                  {f.charAt(0).toUpperCase() + f.slice(1)}
                  {f === 'pending' && guestPendingCount > 0 && (
                    <span style={{ background: '#ef4444', color: 'white', fontSize: '10px', fontWeight: '700', padding: '1px 6px', borderRadius: '100px' }}>
                      {guestPendingCount}
                    </span>
                  )}
                </button>
              ))}
            </div>
            )}

            {/* Stories list */}
            {guestStoriesLoading ? (
              <div style={{ textAlign: 'center', padding: '40px', color: 'rgba(255,255,255,0.4)' }}>
                <span className="spinner-inline"></span> Loading...
              </div>
            ) : guestStories.length === 0 ? (
              <div className="empty-state">
                <div className="empty-icon">💌</div>
                <h3>No {guestStoriesFilter === 'all' ? '' : guestStoriesFilter} stories yet</h3>
                <p>Generate a share link above and send it to guests.</p>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {(ownerTier === 'free' ? guestStories.slice(0, 3) : guestStories).map(story => (
                  <div key={story._id} style={{
                    background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)',
                    borderLeft: story.status === 'approved' ? '3px solid #10b981' : story.status === 'rejected' ? '3px solid rgba(239,68,68,0.5)' : '3px solid #f59e0b',
                    borderRadius: '12px', padding: '16px',
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '10px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <div style={{ width: '36px', height: '36px', borderRadius: '50%', background: 'rgba(139,92,246,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px', fontWeight: '600', color: '#a78bfa', flexShrink: 0 }}>
                          {story.guestName?.split(' ').map(n => n[0]).slice(0,2).join('')}
                        </div>
                        <div>
                          <p style={{ fontSize: '14px', fontWeight: '600', color: '#fff', margin: 0 }}>{story.guestName}</p>
                          <p style={{ fontSize: '11px', color: 'rgba(255,255,255,0.35)', margin: 0 }}>
                            {new Date(story.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                            {story.guestEmail && ` · ${story.guestEmail}`}
                            {story.videoUrl && ' · 📹 video'}
                            {story.photoUrl && !story.videoUrl && ' · 📷 photo'}
                          </p>
                          {story.shareLinkName && (
                            <p style={{ fontSize: '10px', color: '#a78bfa', margin: '2px 0 0', fontWeight: '500' }}>
                              🔗 {story.shareLinkName}
                            </p>
                          )}
                        </div>
                      </div>
                      <span style={{ fontSize: '10px', fontWeight: '600', letterSpacing: '0.06em', textTransform: 'uppercase', padding: '3px 10px', borderRadius: '100px', background: story.status === 'approved' ? 'rgba(16,185,129,0.15)' : story.status === 'rejected' ? 'rgba(239,68,68,0.12)' : 'rgba(245,158,11,0.15)', color: story.status === 'approved' ? '#10b981' : story.status === 'rejected' ? '#ef4444' : '#f59e0b' }}>
                        {story.status}
                      </span>
                    </div>
                    {story.photoUrl && (
                      <a href={story.photoUrl} target="_blank" rel="noopener noreferrer" style={{ display: 'block', marginBottom: '10px', cursor: 'zoom-in' }}>
                        <img src={story.photoUrl} alt="Guest photo" style={{ width: '100%', height: '160px', objectFit: 'cover', borderRadius: '8px' }} />
                      </a>
                    )}
                    {story.videoUrl && (
                      <div style={{ marginBottom: '10px' }}>
                        {story.videoThumbnailUrl ? (
                          <a href={story.videoUrl} target="_blank" rel="noopener noreferrer" style={{ display: 'block', position: 'relative', cursor: 'pointer' }}>
                            <img src={story.videoThumbnailUrl} alt="Video thumbnail" style={{ width: '100%', height: '160px', objectFit: 'cover', borderRadius: '8px' }} />
                            <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.35)', borderRadius: '8px' }}>
                              <div style={{ width: '52px', height: '52px', borderRadius: '50%', background: 'rgba(255,255,255,0.9)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <span style={{ fontSize: '20px', marginLeft: '3px' }}>▶</span>
                              </div>
                            </div>
                          </a>
                        ) : (
                          <a href={story.videoUrl} target="_blank" rel="noopener noreferrer" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', width: '100%', height: '80px', borderRadius: '8px', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', cursor: 'pointer', textDecoration: 'none' }}>
                            <span style={{ fontSize: '20px' }}>🎬</span>
                            <span style={{ color: '#a78bfa', fontSize: '13px', fontWeight: '500' }}>Tap to play video</span>
                          </a>
                        )}
                      </div>
                    )}
                    <p style={{ fontSize: '14px', color: 'rgba(255,255,255,0.7)', lineHeight: '1.65', margin: '0 0 12px' }}>{story.text}</p>
                    {isCircleVaultKeeper && story.status === 'pending' && (
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <button onClick={() => handleGuestStoryApprove(story._id)} disabled={guestStoryAction?.id === story._id} style={{ flex: 1, padding: '9px', background: 'rgba(16,185,129,0.15)', border: '1px solid rgba(16,185,129,0.25)', borderRadius: '9px', color: '#10b981', fontSize: '13px', fontWeight: '500', cursor: 'pointer' }}>
                          {guestStoryAction?.id === story._id && guestStoryAction?.action === 'approving' ? '...' : '✓ Approve'}
                        </button>
                        <button onClick={() => handleGuestStoryReject(story._id)} disabled={guestStoryAction?.id === story._id} style={{ flex: 1, padding: '9px', background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: '9px', color: '#ef4444', fontSize: '13px', fontWeight: '500', cursor: 'pointer' }}>
                          {guestStoryAction?.id === story._id && guestStoryAction?.action === 'rejecting' ? '...' : '✕ Hide'}
                        </button>
                      </div>
                    )}
                    {isCircleVaultKeeper && story.status === 'rejected' && (
                      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <button onClick={() => handleGuestStoryDelete(story._id)} disabled={guestStoryAction?.id === story._id} style={{ padding: '7px 14px', background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: '9px', color: '#ef4444', fontSize: '12px', fontWeight: '500', cursor: 'pointer' }}>
                          {guestStoryAction?.id === story._id && guestStoryAction?.action === 'deleting' ? '...' : '🗑 Delete Permanently'}
                        </button>
                      </div>
                    )}
                    {isCircleVaultKeeper && story.status === 'approved' && (
                      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <button onClick={() => handleGuestStoryDelete(story._id)} disabled={guestStoryAction?.id === story._id} style={{ padding: '7px 14px', background: 'rgba(239,68,68,0.05)', border: '1px solid rgba(239,68,68,0.15)', borderRadius: '9px', color: 'rgba(239,68,68,0.6)', fontSize: '12px', fontWeight: '500', cursor: 'pointer' }}>
                          {guestStoryAction?.id === story._id && guestStoryAction?.action === 'deleting' ? '...' : '🗑 Delete'}
                        </button>
                      </div>
                    )}
                  </div>
                ))}

                {/* Free tier gate — show blurred preview + upgrade prompt */}
                {ownerTier === 'free' && guestStories.length > 3 && (
                  <div style={{ position: 'relative' }}>
                    {/* Blurred preview of next story */}
                    <div style={{
                      background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)',
                      borderLeft: '3px solid rgba(139,92,246,0.3)',
                      borderRadius: '12px', padding: '16px',
                      filter: 'blur(6px)', opacity: 0.5,
                      pointerEvents: 'none', userSelect: 'none'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
                        <div style={{ width: '36px', height: '36px', borderRadius: '50%', background: 'rgba(139,92,246,0.2)' }}></div>
                        <div>
                          <div style={{ width: '120px', height: '14px', background: 'rgba(255,255,255,0.1)', borderRadius: '4px', marginBottom: '6px' }}></div>
                          <div style={{ width: '80px', height: '10px', background: 'rgba(255,255,255,0.06)', borderRadius: '4px' }}></div>
                        </div>
                      </div>
                      <div style={{ width: '100%', height: '12px', background: 'rgba(255,255,255,0.06)', borderRadius: '4px', marginBottom: '8px' }}></div>
                      <div style={{ width: '85%', height: '12px', background: 'rgba(255,255,255,0.04)', borderRadius: '4px' }}></div>
                    </div>
                    {/* Upgrade overlay */}
                    <div style={{
                      position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column',
                      alignItems: 'center', justifyContent: 'center',
                      background: 'rgba(8,6,14,0.6)', borderRadius: '12px',
                    }}>
                      <p style={{ color: 'rgba(255,255,255,0.7)', fontSize: '14px', fontWeight: '500', marginBottom: '4px' }}>
                        +{guestStories.length - 3} more {guestStories.length - 3 === 1 ? 'memory' : 'memories'}
                      </p>
                      <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: '12px', marginBottom: '12px' }}>
                        Upgrade to see all shared memories
                      </p>
                      <button
                        onClick={() => {
                          setUpgradeMessage('Shared Memories are fully available on Legacy Keeper and Legacy Vault plans. Upgrade to view, approve, and manage all guest stories.');
                          setShowUpgradeModal(true);
                        }}
                        style={{
                          padding: '8px 20px', borderRadius: '8px', border: 'none', cursor: 'pointer',
                          background: 'linear-gradient(135deg, #8b5cf6, #6d28d9)',
                          color: '#fff', fontSize: '13px', fontWeight: '600'
                        }}
                      >
                        🚀 Upgrade Plan
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Stories Tab */}
        {activeTab === 'stories' && (
          <div className="stories-tab">
            {stories.length === 0 && !hasActiveFilters ? (
              <div className="empty-state">
                <div className="empty-icon">📖</div>
                <h3>{t('noStoriesYet')}</h3>
                <p>{t('startRecordingFirst')}</p>
                <button 
                  className="btn-primary"
                  onClick={() => setActiveTab('record')}
                >
                  {t('recordStory')}
                </button>
              </div>
            ) : (
              <>
                {/* Search and Filter Bar */}
                <div className="stories-toolbar">
                  <div className="search-box">
                    <span className="search-icon">🔍</span>
                    <input
                      type="text"
                      placeholder={t('searchStories')}
                      value={searchQuery}
                      onChange={handleSearchChange}
                    />
                    {searchQuery && (
                      <button 
                        className="search-clear"
                        onClick={() => { setSearchQuery(''); searchStories({ search: '' }); }}
                      >
                        ✕
                      </button>
                    )}
                  </div>
                  
                  <button 
                    className={`btn-filter ${showFilters ? 'active' : ''} ${hasActiveFilters ? 'has-filters' : ''}`}
                    onClick={() => setShowFilters(!showFilters)}
                  >
                    ⚙️ Filters {hasActiveFilters && <span className="filter-badge">•</span>}
                  </button>
                </div>

                {/* Expanded Filters */}
                {showFilters && (
                  <div className="filters-panel">
                    <div className="filter-group">
                      <label>{t('storyteller')}</label>
                      <select 
                        value={filterStoryteller}
                        onChange={(e) => handleFilterChange('storyteller', e.target.value)}
                      >
                        <option value="">{t('allMembers')}</option>
                        {circle?.members?.map((member) => (
                          <option 
                            key={member.userId?._id || member.userId} 
                            value={member.userId?._id || member.userId}
                          >
                            {member.userId?.fullName || member.userId?.displayName || t('unknown')}
                          </option>
                        ))}
                      </select>
                    </div>
                    
                    <div className="filter-group">
                      <label>{t('sortBy')}</label>
                      <select 
                        value={filterSort}
                        onChange={(e) => handleFilterChange('sort', e.target.value)}
                      >
                        <option value="newest">{t('newestFirst')}</option>
                        <option value="oldest">{t('oldestFirst')}</option>
                        <option value="alpha">A-Z</option>
                        <option value="alpha-desc">Z-A</option>
                      </select>
                    </div>

                    {hasActiveFilters && (
                      <button className="btn-clear-filters" onClick={clearFilters}>
                        {t('clearFilters')}
                      </button>
                    )}
                  </div>
                )}

                <div className="stories-header">
                  <span className="stories-count">
                    {stories.length}{totalStories > stories.length ? ` of ${totalStories}` : ''} {totalStories === 1 ? t('story') : t('stories')}
                    {hasActiveFilters && ` ${t('storiesFound')}`}
                  </span>
                  <button 
                    className="btn-export-book"
                    onClick={() => setShowExportModal(true)}
                  >
                    📚 {t('exportMemoryBook')}
                  </button>
                </div>

                {stories.length === 0 && hasActiveFilters ? (
                  <div className="no-results">
                    <p>{t('noStoriesMatch')}</p>
                    <button className="btn-secondary" onClick={clearFilters}>
                      {t('clearFilters')}
                    </button>
                  </div>
                ) : (
                  <>
                    <div className="stories-list">
                      {stories.map((story) => (
                        <StoryCard 
                          key={story._id} 
                          story={story}
                        />
                      ))}
                    </div>
                    {currentPage < totalPages && (
                      <div className="load-more-container">
                        <button
                          className="btn-load-more"
                          onClick={loadMoreStories}
                          disabled={loadingMore}
                        >
                          {loadingMore ? (
                            <><span className="spinner-inline"></span> Loading...</>
                          ) : (
                            `Load More Stories (${totalStories - stories.length} remaining)`
                          )}
                        </button>
                      </div>
                    )}
                  </>
                )}
              </>
            )}
          </div>
        )}

        {/* Prompts Tab */}
        {activeTab === 'prompts' && (
          <div className="prompts-tab">
            {!selectedCategory ? (
              <>
                <div className="prompts-header">
                  <h2>💡 Story Prompts</h2>
                  <p>{t('chooseCategoryPrompt')}</p>
                </div>
                
                {categories.length === 0 ? (
                  <div className="loading-prompts">{t('loading')}</div>
                ) : (
                  <div className="categories-grid">
                    {categories.map((cat) => (
                      <div 
                        key={cat.id}
                        className="category-card"
                        onClick={() => loadPromptsByCategory(cat)}
                      >
                        <div className="category-emoji">{cat.emoji}</div>
                        <h3>{cat.label}</h3>
                        <p>{cat.description}</p>
                        <span className="category-count">{cat.count} prompts</span>
                      </div>
                    ))}
                  </div>
                )}
              </>
            ) : (
              <>
                <div className="prompts-header">
                  <button 
                    className="btn-back-prompts"
                    onClick={handleBackToCategories}
                  >
                    ← {t('back')}
                  </button>
                  <div className="prompts-header-title">
                    <span className="category-emoji-large">{selectedCategory.emoji}</span>
                    <h2>{selectedCategory.label}</h2>
                  </div>
                </div>

                {loadingPrompts ? (
                  <div className="loading-prompts">{t('loading')}</div>
                ) : prompts.length === 0 ? (
                  <div className="empty-prompts">No prompts found in this category</div>
                ) : (
                  <div className="prompts-list">
                    {prompts.map((prompt) => (
                      <div 
                        key={prompt._id}
                        className="prompt-card"
                        onClick={() => handleSelectPrompt(prompt)}
                      >
                        <p className="prompt-text">{prompt.text}</p>
                        <div className="prompt-meta">
                          <span className={`prompt-difficulty ${prompt.difficulty}`}>
                            {prompt.difficulty === 'easy' ? '🟢 Easy' : 
                             prompt.difficulty === 'deep' ? '🔴 Deep' : '🟡 Medium'}
                          </span>
                          {prompt.usageCount > 0 && (
                            <span className="prompt-usage">Used {prompt.usageCount}x</span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* Record Tab */}
        {activeTab === 'record' && (
          <div className="record-tab">
            {showSuccessMessage && (
              <div className="upload-success-banner">
                <span className="success-icon">✅</span>
                <div>
                  <strong>Story saved successfully!</strong>
                  <p>Your recording has been uploaded and transcribed. You can find it in the Stories tab.</p>
                </div>
              </div>
            )}


{pendingUpload && !isRecording && !audioBlob && (
  <div className="recording-alert" style={{
    background: 'rgba(251, 146, 60, 0.15)',
    border: '1px solid rgba(251, 146, 60, 0.3)',
    color: '#fb923c',
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  }}>
    <div>
      <strong>📱 Unsaved recording found</strong>
      <p style={{ margin: '4px 0 0', fontSize: '12px', opacity: 0.8 }}>
        A recording from {new Date(pendingUpload.cachedAt).toLocaleString()} wasn't uploaded.
      </p>
    </div>
    {cachedAudioUrl && (
      <audio src={cachedAudioUrl} controls style={{ width: '100%' }} />
    )}
    <div style={{ display: 'flex', gap: '8px', flexShrink: 0 }}>
      <button
        onClick={() => handleUpload(true)}
        disabled={uploading}
        style={{
          background: '#fb923c', color: '#000', border: 'none',
          borderRadius: '6px', padding: '8px 14px', fontSize: '13px',
          fontWeight: '600', cursor: 'pointer',
        }}
      >
        {uploading ? 'Uploading...' : 'Retry Upload'}
      </button>
      <button
        onClick={async () => {
          if (window.confirm('Discard this recording? This cannot be undone.')) {
            await clearCache();
            setPendingUpload(null);
            if (cachedAudioUrl) URL.revokeObjectURL(cachedAudioUrl);
            setCachedAudioUrl(null);
          }
        }}
        style={{
          background: 'transparent', color: '#fb923c',
          border: '1px solid rgba(251, 146, 60, 0.3)',
          borderRadius: '6px', padding: '8px 12px', fontSize: '13px',
          cursor: 'pointer',
        }}
      >
        Discard
      </button>
    </div>
  </div>
)}

            <div className="record-card">
              <h2>{t('recordStory')}</h2>
              {selectedPrompt && (
                <div className="selected-prompt-banner">
                  <span className="prompt-label">Responding to:</span>
                  <p>{selectedPrompt.text}</p>
                  <button 
                    className="btn-clear-prompt"
                    onClick={() => { setSelectedPrompt(null); setStoryTitle(''); }}
                  >
                    ✕
                  </button>
                </div>
              )}
              
              <div className="form-group">
                <label>{t('storyTitle')}</label>
                <input
                  type="text"
                  value={storyTitle}
                  onChange={(e) => setStoryTitle(e.target.value)}
                  placeholder={t('storyTitlePlaceholder')}
                  disabled={isRecording || uploading}
                />
              </div>

              <div className="recording-area">
                {showRecordingAlert && (
                  <div className="recording-alert">
                    📱 Keep your screen on during recording. Locking your phone may interrupt the audio capture.
                  </div>
                )}
                <div className="recording-time" style={duration >= WARN_RECORDING_SECONDS ? { color: '#ef4444' } : {}}>
                  {formattedDuration}
                </div>
                {isRecording && duration >= WARN_RECORDING_SECONDS && (
                  <div className="recording-limit-warning">
                    ⚠️ {Math.floor((MAX_RECORDING_SECONDS - duration) / 60)} minute{Math.floor((MAX_RECORDING_SECONDS - duration) / 60) !== 1 ? 's' : ''} remaining — 90 min max
                  </div>
                )}
                <div className="recording-status">
                  {isRecording ? (isPaused ? '⏸️ Paused' : '🔴 Recording...') : audioBlob ? 'Recording complete' : 'Tap to start'}
                </div>

                {!audioBlob ? (
                  <>
                  <div className="recording-buttons">
                    <button
                      className={`btn-record ${isRecording ? 'recording' : ''}`}
                      onClick={isRecording ? stopRecording : handleStartRecording}
                      disabled={uploading}
                    >
                      {isRecording ? (
                        <span className="stop-icon">◼</span>
                      ) : (
                        <span className="mic-icon">🎤</span>
                      )}
                    </button>
                    {isRecording && (
                      <button
                        className={`btn-pause ${isPaused ? 'paused' : ''}`}
                        onClick={isPaused ? resumeRecording : pauseRecording}
                      >
                        {isPaused ? '▶️ Resume' : '⏸️ Pause'}
                      </button>
                    )}
                  </div>
                  {!isRecording && (
                    <p className="recording-max-note">⏱️ Max recording length: 90 minutes</p>
                  )}
                  </>
                ) : (
                  <div className="recording-preview">
                    <audio src={audioUrl} controls />
                    
                    <div className="preview-actions">
                      <button 
                        className="btn-secondary"
                        onClick={handleCancelRecording}
                        disabled={uploading}
                      >
                        Discard
                      </button>
                      <button 
                        className="btn-primary"
                        onClick={handleUpload}
                        disabled={uploading}
                        style={uploading ? { opacity: 0.6, cursor: 'not-allowed' } : {}}
                      >
                        {uploading ? (
                          <>
                            <span className="spinner-inline"></span>
                            {uploadProgress}
                          </>
                        ) : 'Save & Transcribe'}
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {isRecording && (
                <div className="recording-tip">
                  💡 {t('recordingTip')}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Members Tab */}
        {activeTab === 'members' && (
          <div className="members-tab">
            <div className="members-section-header">
              <h3>👥 {t('membersTab')} ({circle?.members?.length || 0})</h3>
              {currentUserRole === 'vaultKeeper' && (
                <button 
                  className="btn-invite"
                  onClick={() => setShowInviteModal(true)}
                >
                  + {t('invite')}
                </button>
              )}
            </div>

            <div className="members-list">
              {circle?.members?.map((member, index) => {
                const memberId = member.userId?._id || member.userId;
                const currentUser = JSON.parse(localStorage.getItem('user'));
                const isCurrentUser = memberId === currentUser?._id;
                const memberIsOwner = isOwner(member);
                
                return (
                  <div key={index} className="member-card">
                    <div className="member-avatar">
                      {member.profilePhotoUrl ? (
                        <img src={member.profilePhotoUrl} alt={member.userId?.fullName || member.userId?.displayName} className="member-avatar-img" />
                      ) : (
                        (member.userId?.fullName || member.userId?.displayName)?.charAt(0) || '?'
                      )}
                    </div>
                    <div className="member-info">
                      <h4>
                        {member.userId?.fullName || member.userId?.displayName || t('unknown')}
                        {memberIsOwner && <span className="owner-badge">{t('owner')}</span>}
                        {isCurrentUser && <span className="you-badge">{t('you')}</span>}
                      </h4>
                      <p>{getRoleLabel(member.role)} {member.relationship && `• ${member.relationship}`}</p>
                    </div>
                    
                    {currentUserRole === 'vaultKeeper' && !memberIsOwner && !isCurrentUser && (
                      <div className="member-actions">
                        <button 
                          className="btn-icon"
                          onClick={() => openRoleModal(member)}
                          title="Change Role"
                        >
                          ⚙️
                        </button>
                        <button 
                          className="btn-icon btn-icon-danger"
                          onClick={() => openRemoveModal(member)}
                          title="Remove Member"
                        >
                          ✕
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {currentUserRole !== 'vaultKeeper' && (
              <p className="members-note">{t('onlyVaultKeepers')}</p>
            )}

            {/* Pending Invites */}
            {pendingInvites.length > 0 && (
              <div className="pending-invites-section">
                <h3>📧 {t('pendingInvites')}</h3>
                <div className="pending-invites-list">
                  {pendingInvites.map((invite) => (
                    <div key={invite._id} className="pending-invite-card">
                      <div className="invite-info">
                        <p className="invite-email">{invite.email}</p>
                        <p className="invite-meta">{invite.role} • Expires {new Date(invite.expiresAt).toLocaleDateString()}</p>
                      </div>
                      {currentUserRole === 'vaultKeeper' && (
                        <button 
                          className="btn-cancel-invite"
                          onClick={() => handleCancelInvite(invite._id)}
                        >
                          ✕
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Transfer Ownership — only visible to current owner */}
            {(circle?.ownerId?._id || circle?.ownerId)?.toString() === JSON.parse(localStorage.getItem('user') || '{}')._id?.toString() && (
              <div style={{ marginTop: '20px', padding: '16px', background: 'rgba(251,191,36,0.06)', border: '1px solid rgba(251,191,36,0.15)', borderRadius: '12px' }}>
                <h3 style={{ fontSize: '14px', color: '#fbbf24', marginBottom: '6px' }}>👑 Circle Ownership</h3>
                <p style={{ color: 'rgba(255,255,255,0.45)', fontSize: '13px', marginBottom: '12px' }}>
                  Transfer ownership of this circle to another member. You'll remain a Vault Keeper.
                </p>
                <button
                  onClick={() => { setTransferTargetId(''); setShowTransferModal(true); }}
                  style={{
                    padding: '8px 16px', borderRadius: '8px',
                    border: '1px solid rgba(251,191,36,0.3)',
                    background: 'transparent', color: '#fbbf24',
                    fontSize: '13px', fontWeight: '500', cursor: 'pointer'
                  }}
                >
                  Transfer Ownership
                </button>
              </div>
            )}

            {/* Voice Profiles Section */}
            <div className="voice-profiles-section">
              <h3>🎙️ {t('voiceProfiles')}</h3>
              <p className="section-description">
                Create voice clones to generate AI narrations of stories
              </p>
              
              {voiceProfiles.length > 0 ? (
                <div className="voice-profiles-list">
                  {voiceProfiles.map((profile) => (
                    <div key={profile._id} className="voice-profile-card">
                      <div className="voice-icon">🎤</div>
                      <div className="voice-info">
                        <h4>{profile.name || profile.userId?.displayName || 'Unnamed Voice'}</h4>
                        <p>{profile.userId?.displayName ? `Created by ${profile.userId.displayName} • ` : ''}{profile.sampleCount || 1} sample(s) • {profile.status}</p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="no-voices">
                  <p>{t('noVoiceProfilesYet')}</p>
                </div>
              )}

              <button 
                className="btn-create-voice"
                onClick={() => setShowVoiceModal(true)}
              >
                + Create Voice Profile
              </button>
            </div>
          </div>
        )}

        {/* Family Tree Tab */}
        {activeTab === 'tree' && (
          <div className="tree-tab">
            <div className="tree-header">
              <h2>🌳 Family Tree</h2>
              <p>{t('visualizeRelationships')}</p>
              {treeMembers.length > 0 && (
                <button 
                  className="btn-export-book"
                  onClick={() => {
                    const token = localStorage.getItem('token');
                    const url = `${API_URL}/family/${id}/tree/export/pdf?token=${token}`;
                    window.open(url, '_blank');
                  }}
                  style={{ marginTop: '8px' }}
                >
                  📄 {t('exportTreePDF')}
                </button>
              )}
            </div>

            {loadingTree ? (
              <div className="loading-tree">{t('loading')}</div>
            ) : treeMembers.length === 0 ? (
              <div className="empty-tree">
                <p>{t('noTreeMembers')}</p>
                <p>{t('addMembersFirst')}</p>
              </div>
            ) : (
              <div className="tree-container">
                {groupByGeneration(treeMembers).map(({ generation, members }) => (
                  <div key={generation} className="generation-row">
                    <div className="generation-label">
                      {getGenerationLabel(generation)}
                    </div>
                    <div className="generation-members">
                      {(() => {
                        const renderedAsSpouse = new Set();
                        // Pre-scan: build reverse spouse lookup for one-way links
                        const reverseSpouse = {};
                        members.forEach(m => {
                          if (m.spouseId) reverseSpouse[m.spouseId] = m.id;
                        });
                        return members.map((member) => {
                          // Skip if already rendered as someone else's spouse
                          if (renderedAsSpouse.has(member.id)) return null;

                          // Find spouse: either member links to spouse, or spouse links to member
                          let spouse = member.spouseId 
                            ? treeMembers.find(m => m.id === member.spouseId)
                            : null;
                          if (!spouse && reverseSpouse[member.id]) {
                            spouse = treeMembers.find(m => m.id === reverseSpouse[member.id]);
                          }
                          
                          // Mark spouse as rendered so they don't get their own card
                          if (spouse) renderedAsSpouse.add(spouse.id);
                        
                        return (
                          <div key={member.id} className="tree-member-wrapper">
                            <div 
                              className={`tree-member-card ${member.isDeceased ? 'deceased' : ''}`}
                              onClick={() => !member.isTreeOnly && setViewingMemberStories(member)}
                              style={{ cursor: member.isTreeOnly ? 'default' : 'pointer' }}
                            >
                              {currentUserRole === 'vaultKeeper' && (
                                <button 
                                  className="tree-edit-btn"
                                  onClick={(e) => { e.stopPropagation(); handleEditTreeMember(member); }}
                                  title="Edit tree info"
                                >
                                  ✏️
                                </button>
                              )}
                              <div className="tree-member-avatar">
                                {member.profilePhotoUrl ? (
                                  <img src={member.profilePhotoUrl} alt={member.name} className="tree-avatar-img" />
                                ) : (
                                  member.name?.charAt(0) || '?'
                                )}
                              </div>
                              <div className="tree-member-name">{member.name}</div>
                              {member.relationship && (
                                <div className="tree-member-relation">{member.relationship}</div>
                              )}
                              {(member.birthDate || member.birthYear) && (
                                <div className="tree-member-year">
                                  b. {member.birthDate ? new Date(member.birthDate).getFullYear() : member.birthYear}
                                  {member.isDeceased && member.deathDate && (
                                    <> — d. {new Date(member.deathDate).getFullYear()}</>
                                  )}
                                </div>
                              )}
                              {member.birthPlace && (
                                <div className="tree-member-place">📍 {member.birthPlace}</div>
                              )}
                              {member.isDeceased && (
                                <div className="tree-member-deceased">🕊️</div>
                              )}
                              {member.bio && (
                                <div className="tree-member-bio">{member.bio.length > 60 ? member.bio.substring(0, 58) + '…' : member.bio}</div>
                              )}
                              {!member.isTreeOnly && (
                                <div className="tree-stories-hint">
                                  📚 {stories.filter(s => s.storytellerId?._id === member.id || s.storytellerId === member.id).length} stories
                                </div>
                              )}
                            </div>
                            {spouse && (
                              <>
                                <div className="spouse-connector">💑</div>
                                <div 
                                  className={`tree-member-card spouse-card ${spouse.isDeceased ? 'deceased' : ''}`}
                                  onClick={() => !spouse.isTreeOnly && setViewingMemberStories(spouse)}
                                  style={{ cursor: spouse.isTreeOnly ? 'default' : 'pointer' }}
                                >
                                  {currentUserRole === 'vaultKeeper' && (
                                    <button 
                                      className="tree-edit-btn"
                                      onClick={(e) => { e.stopPropagation(); handleEditTreeMember(spouse); }}
                                      title="Edit tree info"
                                    >
                                      ✏️
                                    </button>
                                  )}
                                  <div className="tree-member-avatar">
                                    {spouse.profilePhotoUrl ? (
                                      <img src={spouse.profilePhotoUrl} alt={spouse.name} className="tree-avatar-img" />
                                    ) : (
                                      spouse.name?.charAt(0) || '?'
                                    )}
                                  </div>
                                  <div className="tree-member-name">{spouse.name}</div>
                                  {spouse.relationship && (
                                    <div className="tree-member-relation">{spouse.relationship}</div>
                                  )}
                                  {(spouse.birthDate || spouse.birthYear) && (
                                    <div className="tree-member-year">
                                      b. {spouse.birthDate ? new Date(spouse.birthDate).getFullYear() : spouse.birthYear}
                                      {spouse.isDeceased && spouse.deathDate && (
                                        <> — d. {new Date(spouse.deathDate).getFullYear()}</>
                                      )}
                                    </div>
                                  )}
                                  {spouse.birthPlace && (
                                    <div className="tree-member-place">📍 {spouse.birthPlace}</div>
                                  )}
                                  {spouse.isDeceased && (
                                    <div className="tree-member-deceased">🕊️</div>
                                  )}
                                  {!spouse.isTreeOnly && (
                                    <div className="tree-stories-hint">
                                      📚 {stories.filter(s => s.storytellerId?._id === spouse.id || s.storytellerId === spouse.id).length} stories
                                    </div>
                                  )}
                                </div>
                              </>
                            )}
                          </div>
                        );
                        }).filter(Boolean)
                      })()}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {currentUserRole === 'vaultKeeper' && (
              <div className="tree-help">
                <p>💡 Click on any family member to edit their relationships and generation.</p>
                <button 
                  className="btn-secondary" 
                  onClick={() => setShowAddTreeModal(true)}
                  style={{ marginTop: '12px' }}
                >
                  + Add Person to Tree
                </button>
              </div>
            )}
          </div>
        )}
      </main>

      {/* Create Voice Modal */}
      {showVoiceModal && (
        <div className="modal-overlay" onClick={() => setShowVoiceModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>{t('createVoiceProfile')}</h2>
            <p className="modal-description">
              Upload audio samples to create a voice clone. For best results, upload 1-5 clear audio samples of 30 seconds to 2 minutes each.
            </p>
            
            <form onSubmit={handleCreateVoice}>
              <div className="form-group">
                <label>{t('voiceName')}</label>
                <input
                  type="text"
                  value={voiceName}
                  onChange={(e) => setVoiceName(e.target.value)}
                  placeholder={t('voiceNamePlaceholder')}
                  required
                />
              </div>

              <div className="form-group">
                <label>{t('audioSamples')}</label>
                <input
                  type="file"
                  accept="audio/*"
                  multiple
                  onChange={handleVoiceSampleChange}
                  className="file-input"
                />
                {voiceSamples.length > 0 && (
                  <p className="file-count">{voiceSamples.length} file(s) selected</p>
                )}
              </div>

              <div className="modal-actions">
                <button 
                  type="button" 
                  className="btn-secondary"
                  onClick={() => setShowVoiceModal(false)}
                  disabled={creatingVoice}
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="btn-primary"
                  disabled={creatingVoice}
                >
                  {creatingVoice ? t('creatingVoice') : t('createVoice')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Invite Modal */}
      {showInviteModal && (
        <div className="modal-overlay" onClick={handleCloseInviteModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            {inviteSuccess ? (
              <>
                <div style={{ textAlign: 'center', padding: '20px 0' }}>
                  <div style={{ fontSize: '48px', marginBottom: '16px' }}>✉️</div>
                  <h2>{t('inviteSent')}</h2>
                  <p className="modal-description" style={{ margin: '12px 0 8px' }}>
                    {t('inviteSentDesc')} <strong>{inviteEmail}</strong>
                  </p>
                  <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: '13px', margin: '0 0 24px' }}>
                    {t('inviteJoinLink')} {circle?.name}.
                  </p>
                  <button className="btn-primary" onClick={handleCloseInviteModal}>
                    {t('done')}
                  </button>
                </div>
              </>
            ) : (
              <>
                <h2>📧 {t('inviteFamilyMember')}</h2>
                <p className="modal-description">
                  {t('sendInviteDesc')} {circle?.name}. {t('sendInviteDesc2')}
                </p>

                {inviteError && (
                  <div style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: '10px', padding: '10px 14px', color: '#fca5a5', fontSize: '13px', marginBottom: '16px' }}>
                    {inviteError}
                  </div>
                )}
                
                <form onSubmit={handleSendInvite}>
                  <div className="form-group">
                    <label>{t('emailAddress')}</label>
                    <input
                      type="email"
                      value={inviteEmail}
                      onChange={(e) => setInviteEmail(e.target.value)}
                      placeholder="family@example.com"
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label>{t('role')}</label>
                    <select 
                      value={inviteRole} 
                      onChange={(e) => setInviteRole(e.target.value)}
                    >
                      <option value="contributor">{t('contributorRole')}</option>
                      <option value="storyteller">{t('storytellerRole')}</option>
                      <option value="viewer">{t('viewerRole')}</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label>{t('relationshipOptional')}</label>
                    <input
                      type="text"
                      value={inviteRelationship}
                      onChange={(e) => setInviteRelationship(e.target.value)}
                      placeholder={t('relationshipPlaceholder')}
                    />
                  </div>

                  <div className="modal-actions">
                    <button 
                      type="button" 
                      className="btn-secondary"
                      onClick={handleCloseInviteModal}
                      disabled={sendingInvite}
                    >
                      {t('cancel')}
                    </button>
                    <button 
                      type="submit" 
                      className="btn-primary"
                      disabled={sendingInvite}
                    >
                      {sendingInvite ? t('sending') : t('sendInvite')}
                    </button>
                  </div>
                </form>
              </>
            )}
          </div>
        </div>
      )}

      {/* Change Role Modal */}
      {showRoleModal && selectedMember && (
        <div className="modal-overlay" onClick={() => setShowRoleModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>⚙️ {t('changeRole')}</h2>
            <p className="modal-description">
              {t('changeRoleDesc')} <strong>{selectedMember.userId?.fullName || selectedMember.userId?.displayName}</strong>
            </p>
            
            <div className="form-group">
              <label>{t('newRole')}</label>
              <select 
                value={newRole} 
                onChange={(e) => setNewRole(e.target.value)}
              >
                <option value="vaultKeeper">{t('vaultKeeperRole')}</option>
                <option value="storyteller">{t('storytellerRoleFull')}</option>
                <option value="contributor">{t('contributorRoleFull')}</option>
                <option value="viewer">{t('viewerRoleFull')}</option>
              </select>
            </div>

            <div className="modal-actions">
              <button 
                className="btn-secondary"
                onClick={() => setShowRoleModal(false)}
                disabled={updatingRole}
              >
                {t('cancel')}
              </button>
              <button 
                className="btn-primary"
                onClick={handleChangeRole}
                disabled={updatingRole || newRole === selectedMember.role}
              >
                {updatingRole ? t('updating') : t('updateRole')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Remove Member Modal */}
      {showRemoveModal && selectedMember && (
        <div className="modal-overlay" onClick={() => setShowRemoveModal(false)}>
          <div className="modal-content confirm-modal" onClick={(e) => e.stopPropagation()}>
            <h2>⚠️ {t('removeMember')}</h2>
            <p className="modal-description">
              {t('removeMemberDesc')} <strong>{selectedMember.userId?.fullName || selectedMember.userId?.displayName}</strong> {t('removeMemberDesc2')}
            </p>

            <div className="modal-actions">
              <button 
                className="btn-secondary"
                onClick={() => setShowRemoveModal(false)}
                disabled={removingMember}
              >
                {t('cancel')}
              </button>
              <button 
                className="btn-danger"
                onClick={handleRemoveMember}
                disabled={removingMember}
              >
                {removingMember ? t('removing') : t('removeMember')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Tree Edit Modal */}
      {showTreeEditModal && editingTreeMember && (
        <div className="modal-overlay" onClick={() => setShowTreeEditModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>🌳 {t('editFamilyRelationship')}</h2>
            <p className="modal-description">
              {t('updateTreePosition')} <strong>{editingTreeMember.name}</strong>
            </p>

            {editingTreeMember.isTreeOnly && (
              <div className="form-group">
                <label>Name</label>
                <input
                  type="text"
                  value={editingTreeMember.name || ''}
                  onChange={(e) => setEditingTreeMember({...editingTreeMember, name: e.target.value})}
                  placeholder="Full name"
                />
              </div>
            )}

            <div className="form-group">
              <label>{t('relationshipLabel')}</label>
              <input
                type="text"
                value={editingTreeMember.relationship || ''}
                onChange={(e) => setEditingTreeMember({...editingTreeMember, relationship: e.target.value})}
                placeholder={t('relationshipLabelPlaceholder')}
              />
            </div>

            <div className="form-group">
              <label>{t('generation')}</label>
              <select
                value={editingTreeMember.generation}
                onChange={(e) => setEditingTreeMember({...editingTreeMember, generation: e.target.value})}
              >
                <option value="-3">{t('greatGrandparents')} (-3)</option>
                <option value="-2">{t('grandparents')} (-2)</option>
                <option value="-1">{t('parents')} (-1)</option>
                <option value="0">{t('youAndSiblings')} (0)</option>
                <option value="1">{t('children')} (1)</option>
                <option value="2">{t('grandchildren')} (2)</option>
                <option value="3">{t('greatGrandchildren')} (3)</option>
              </select>
            </div>

            <div className="form-group">
              <label>{t('parent')}</label>
              <select
                value={editingTreeMember.parentId || ''}
                onChange={(e) => setEditingTreeMember({...editingTreeMember, parentId: e.target.value})}
              >
                <option value="">{t('none')}</option>
                {treeMembers
                  .filter(m => m.id !== editingTreeMember.id)
                  .map(m => (
                    <option key={m.id} value={m.id}>{m.name}</option>
                  ))
                }
              </select>
            </div>

            <div className="form-group">
              <label>{t('spouse')}</label>
              <select
                value={editingTreeMember.spouseId || ''}
                onChange={(e) => setEditingTreeMember({...editingTreeMember, spouseId: e.target.value})}
              >
                <option value="">{t('none')}</option>
                {treeMembers
                  .filter(m => m.id !== editingTreeMember.id)
                  .map(m => (
                    <option key={m.id} value={m.id}>{m.name}</option>
                  ))
                }
              </select>
            </div>

            <hr style={{ border: 'none', borderTop: '1px solid rgba(255,255,255,0.08)', margin: '16px 0' }} />

            <div className="form-row">
              <div className="form-group" style={{ flex: 1 }}>
                <label>{t('birthDate')}</label>
                <input
                  type="date"
                  value={editingTreeMember.birthDate ? new Date(editingTreeMember.birthDate).toISOString().split('T')[0] : ''}
                  onChange={(e) => setEditingTreeMember({...editingTreeMember, birthDate: e.target.value || null})}
                />
              </div>
              <div className="form-group" style={{ flex: 1 }}>
                <label>{t('birthPlace')}</label>
                <input
                  type="text"
                  value={editingTreeMember.birthPlace || ''}
                  onChange={(e) => setEditingTreeMember({...editingTreeMember, birthPlace: e.target.value})}
                  placeholder="e.g., Chicago, IL"
                />
              </div>
            </div>

            <div className="form-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={editingTreeMember.isDeceased || false}
                  onChange={(e) => setEditingTreeMember({
                    ...editingTreeMember, 
                    isDeceased: e.target.checked,
                    deathDate: e.target.checked ? editingTreeMember.deathDate : null,
                    deathPlace: e.target.checked ? editingTreeMember.deathPlace : null
                  })}
                />
                <span>{t('markDeceased')} 🕊️</span>
              </label>
            </div>

            {editingTreeMember.isDeceased && (
              <div className="form-row">
                <div className="form-group" style={{ flex: 1 }}>
                  <label>{t('deathDate')}</label>
                  <input
                    type="date"
                    value={editingTreeMember.deathDate ? new Date(editingTreeMember.deathDate).toISOString().split('T')[0] : ''}
                    onChange={(e) => setEditingTreeMember({...editingTreeMember, deathDate: e.target.value || null})}
                  />
                </div>
                <div className="form-group" style={{ flex: 1 }}>
                  <label>{t('deathPlace')}</label>
                  <input
                    type="text"
                    value={editingTreeMember.deathPlace || ''}
                    onChange={(e) => setEditingTreeMember({...editingTreeMember, deathPlace: e.target.value})}
                    placeholder="e.g., Los Angeles, CA"
                  />
                </div>
              </div>
            )}

            <div className="form-group">
              <label>{t('bioOptional')}</label>
              <textarea
                value={editingTreeMember.bio || ''}
                onChange={(e) => setEditingTreeMember({...editingTreeMember, bio: e.target.value})}
                placeholder={t('bioPlaceholderTree')}
                rows={2}
                maxLength={500}
              />
            </div>

            <div className="modal-actions">
              {editingTreeMember.isTreeOnly && (
                <button 
                  className="btn-danger"
                  onClick={() => handleDeleteTreeMember(editingTreeMember.id)}
                  style={{ marginRight: 'auto' }}
                >
                  Remove
                </button>
              )}
              <button 
                className="btn-secondary"
                onClick={() => setShowTreeEditModal(false)}
              >
                {t('cancel')}
              </button>
              <button 
                className="btn-primary"
                onClick={handleSaveTreeMember}
              >
                {t('saveChanges')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Tree-Only Member Modal */}
      {showAddTreeModal && (
        <div className="modal-overlay" onClick={() => setShowAddTreeModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>🌳 Add Person to Tree</h2>
            <p className="modal-description">
              Add a family member who doesn't have an account. They'll appear in the family tree and PDF exports.
            </p>

            <div className="form-group">
              <label>Name *</label>
              <input
                type="text"
                value={newTreeMember.name}
                onChange={(e) => setNewTreeMember({...newTreeMember, name: e.target.value})}
                placeholder="Full name"
                autoFocus
              />
            </div>

            <div className="form-group">
              <label>Relationship</label>
              <input
                type="text"
                value={newTreeMember.relationship}
                onChange={(e) => setNewTreeMember({...newTreeMember, relationship: e.target.value})}
                placeholder="e.g., Father, Grandmother, Uncle"
              />
            </div>

            <div className="form-group">
              <label>Generation</label>
              <select
                value={newTreeMember.generation}
                onChange={(e) => setNewTreeMember({...newTreeMember, generation: e.target.value})}
              >
                <option value="-3">Great-Grandparents (-3)</option>
                <option value="-2">Grandparents (-2)</option>
                <option value="-1">Parents (-1)</option>
                <option value="0">You & Siblings (0)</option>
                <option value="1">Children (1)</option>
                <option value="2">Grandchildren (2)</option>
                <option value="3">Great-Grandchildren (3)</option>
              </select>
            </div>

            <div className="form-group">
              <label>Parent</label>
              <select
                value={newTreeMember.parentId}
                onChange={(e) => setNewTreeMember({...newTreeMember, parentId: e.target.value})}
              >
                <option value="">None</option>
                {treeMembers.map(m => (
                  <option key={m.id} value={m.id}>{m.name}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Spouse</label>
              <select
                value={newTreeMember.spouseId}
                onChange={(e) => setNewTreeMember({...newTreeMember, spouseId: e.target.value})}
              >
                <option value="">None</option>
                {treeMembers.map(m => (
                  <option key={m.id} value={m.id}>{m.name}</option>
                ))}
              </select>
            </div>

            <div className="form-row">
              <div className="form-group" style={{ flex: 1 }}>
                <label>Birth Year</label>
                <input
                  type="number"
                  value={newTreeMember.birthYear}
                  onChange={(e) => setNewTreeMember({...newTreeMember, birthYear: e.target.value})}
                  placeholder="1950"
                />
              </div>
              <div className="form-group" style={{ flex: 1 }}>
                <label>Birth Place</label>
                <input
                  type="text"
                  value={newTreeMember.birthPlace}
                  onChange={(e) => setNewTreeMember({...newTreeMember, birthPlace: e.target.value})}
                  placeholder="City, State"
                />
              </div>
            </div>

            <div className="form-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={newTreeMember.isDeceased}
                  onChange={(e) => setNewTreeMember({...newTreeMember, isDeceased: e.target.checked})}
                />
                Deceased
              </label>
            </div>

            <div className="form-group">
              <label>Bio</label>
              <textarea
                value={newTreeMember.bio}
                onChange={(e) => setNewTreeMember({...newTreeMember, bio: e.target.value})}
                placeholder="Short bio or notes..."
                rows={2}
              />
            </div>

            <div className="modal-actions">
              <button 
                className="btn-secondary"
                onClick={() => setShowAddTreeModal(false)}
              >
                Cancel
              </button>
              <button 
                className="btn-primary"
                onClick={handleAddTreeMember}
                disabled={!newTreeMember.name.trim()}
              >
                Add to Tree
              </button>
            </div>
          </div>
        </div>
      )}


      {/* Member Stories Modal */}
      {viewingMemberStories && (
        <div className="modal-overlay" onClick={() => setViewingMemberStories(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>📚 {viewingMemberStories.name}'s Stories</h2>
            {(() => {
              const memberStories = stories.filter(s => 
                s.storytellerId?._id === viewingMemberStories.id || 
                s.storytellerId === viewingMemberStories.id
              );
              if (memberStories.length === 0) {
                return (
                  <div className="empty-state" style={{ padding: '24px 0', textAlign: 'center' }}>
                    <p style={{ color: 'rgba(255,255,255,0.5)' }}>No stories recorded yet.</p>
                  </div>
                );
              }
              return (
                <div className="member-stories-list">
                  {memberStories.map(story => (
                    <div 
                      key={story._id} 
                      className="member-story-item"
                      onClick={() => { setViewingMemberStories(null); navigate(`/story/${story._id}`); }}
                    >
                      <div className="member-story-info">
                        <div className="member-story-title">{story.title || 'Untitled Story'}</div>
                        <div className="member-story-meta">
                          {story.category && <span className="member-story-category">{story.category}</span>}
                          <span className="member-story-date">
                            {new Date(story.createdAt).toLocaleDateString()}
                          </span>
                          {story.duration && (
                            <span className="member-story-duration">
                              {Math.round(story.duration / 60)} min
                            </span>
                          )}
                        </div>
                      </div>
                      <span className="member-story-arrow">→</span>
                    </div>
                  ))}
                </div>
              );
            })()}
            <div className="modal-actions">
              <button 
                className="btn-secondary"
                onClick={() => setViewingMemberStories(null)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Export Memory Book Modal */}
      {showExportModal && (
        <div className="modal-overlay" onClick={() => setShowExportModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>📚 {t('exportMemoryBookTitle')}</h2>
            <p className="modal-description">
              {t('exportDescription')}
            </p>

            <div className="form-group">
              <label>{t('storyVersionLabel')}</label>
              <select value={exportVersion} onChange={(e) => setExportVersion(e.target.value)}>
                <option value="literary">📖 {t('literary')}</option>
                <option value="polished">✨ {t('polished')}</option>
                <option value="raw">📝 {t('original')}</option>
              </select>
            </div>

            <div className="form-group">
              <label>{t('filterByStoryteller')}</label>
              <select value={exportStoryteller} onChange={(e) => setExportStoryteller(e.target.value)}>
                <option value="">{t('allStorytellers')}</option>
                {circle?.members?.map((member) => (
                  <option key={member.userId?._id || member.userId} value={member.userId?._id || member.userId}>
                    {member.userId?.fullName || member.userId?.displayName || t('unknown')}
                  </option>
                ))}
              </select>
            </div>

            {storyCategories.length > 0 && (
              <div className="form-group">
                <label>{t('filterByCategory')}</label>
                <select value={exportCategory} onChange={(e) => setExportCategory(e.target.value)}>
                  <option value="">{t('allCategories')}</option>
                  {storyCategories.map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>
            )}

            <div className="form-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={exportIncludePhotos}
                  onChange={(e) => setExportIncludePhotos(e.target.checked)}
                />
                <span>{t('includePhotos')}</span>
              </label>
            </div>

            <div className="form-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={exportGroupByCategory}
                  onChange={(e) => setExportGroupByCategory(e.target.checked)}
                />
                <span>{t('groupByCategory')}</span>
              </label>
            </div>

            <div className="modal-actions">
              <button className="btn-secondary" onClick={() => setShowExportModal(false)}>
                {t('cancel')}
              </button>
              <button className="btn-primary" onClick={handleExportMemoryBook}>
                📥 {t('generatePDF')}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Share Token Modal */}
      {showShareModal && (
        <div className="modal-overlay" onClick={() => setShowShareModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>🔗 Create Share Link</h2>
            <p className="modal-description">
              Generate a QR code and link for guests to share their memories — no account needed.
            </p>

            <div className="form-group">
              <label>Event Type</label>
              <select value={tokenForm.eventType} onChange={(e) => setTokenForm({...tokenForm, eventType: e.target.value})}>
                <option value="birthday">🎂 Birthday</option>
                <option value="anniversary">💍 Anniversary</option>
                <option value="funeral">🕊️ Memorial / Funeral</option>
                <option value="graduation">🎓 Graduation</option>
                <option value="wedding">💒 Wedding</option>
                <option value="reunion">👨‍👩‍👧‍👦 Reunion</option>
                <option value="other">✨ Other</option>
              </select>
            </div>

            <div className="form-group">
              <label>Event Name *</label>
              <input
                type="text"
                value={tokenForm.eventName}
                onChange={(e) => setTokenForm({...tokenForm, eventName: e.target.value})}
                placeholder="e.g. Grandma Rosa's 80th Birthday"
              />
            </div>

            <div className="form-group">
              <label>Honoree Name (optional)</label>
              <input
                type="text"
                value={tokenForm.subjectName}
                onChange={(e) => setTokenForm({...tokenForm, subjectName: e.target.value})}
                placeholder="e.g. Rosa Ornelas"
              />
            </div>

            <div className="form-group">
              <label>Link Expires After</label>
              <select value={tokenForm.expiresInDays} onChange={(e) => setTokenForm({...tokenForm, expiresInDays: parseInt(e.target.value)})}>
                <option value={7}>7 days</option>
                <option value={14}>14 days</option>
                <option value={30}>30 days</option>
                <option value={90}>90 days</option>
                <option value={365}>1 year</option>
              </select>
            </div>

            <div className="form-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={tokenForm.allowVideo}
                  onChange={(e) => setTokenForm({...tokenForm, allowVideo: e.target.checked})}
                />
                <span>Allow guests to submit video stories</span>
              </label>
              <p style={{ color: 'rgba(255,255,255,0.35)', fontSize: '12px', marginTop: '4px' }}>
                Videos are transcoded to 720p and capped at 2 minutes.
              </p>
            </div>

            <div className="modal-actions">
              <button className="btn-secondary" onClick={() => setShowShareModal(false)} disabled={creatingToken}>
                Cancel
              </button>
              <button className="btn-primary" onClick={handleCreateShareToken} disabled={creatingToken || !tokenForm.eventName.trim()}>
                {creatingToken ? <><span className="spinner-inline"></span>Creating...</> : '✨ Generate QR & Link'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Photo Popup Modal */}
      {showPhotoModal && circleAvatar && (
        <div className="modal-overlay" onClick={() => setShowPhotoModal(false)}>
          <div className="photo-modal" onClick={(e) => e.stopPropagation()}>
            <button className="photo-modal-close" onClick={() => setShowPhotoModal(false)}>✕</button>
            <img src={circleAvatar} alt={circle?.name} className="photo-modal-img" />
            <p className="photo-modal-name">{circle?.name}</p>
            {currentUserRole === 'vaultKeeper' && (
              <button 
                className="photo-modal-remove" 
                onClick={() => { handleRemoveAvatar(); setShowPhotoModal(false); }}
              >
                Remove Photo
              </button>
            )}
          </div>
        </div>
      )}
      {/* Transfer Ownership Modal */}
      {showTransferModal && (
        <div className="modal-overlay" onClick={() => setShowTransferModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '440px' }}>
            <div style={{ textAlign: 'center', marginBottom: '20px' }}>
              <div style={{ fontSize: '48px', marginBottom: '12px' }}>👑</div>
              <h2 style={{ marginBottom: '8px' }}>Transfer Ownership</h2>
              <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '14px', lineHeight: '1.7' }}>
                Select a member to become the new owner of <strong style={{ color: '#fbbf24' }}>{circle?.name}</strong>. You'll remain a Vault Keeper with admin access.
              </p>
            </div>

            <div className="form-group">
              <label>New Owner</label>
              <select
                value={transferTargetId}
                onChange={(e) => setTransferTargetId(e.target.value)}
                style={{
                  width: '100%', padding: '10px 12px',
                  background: 'rgba(255,255,255,0.08)',
                  border: '1px solid rgba(255,255,255,0.15)',
                  borderRadius: '8px', color: '#fff',
                  fontSize: '14px', cursor: 'pointer'
                }}
              >
                <option value="">— Select a member —</option>
                {circle?.members?.filter(m => {
                  const mId = (m.userId?._id || m.userId)?.toString();
                  const currentUserId = JSON.parse(localStorage.getItem('user') || '{}')._id;
                  return mId !== currentUserId;
                }).map(m => {
                  const mId = (m.userId?._id || m.userId)?.toString();
                  return (
                    <option key={mId} value={mId}>
                      {m.userId?.fullName || m.userId?.displayName || 'Unknown'} ({m.role})
                    </option>
                  );
                })}
              </select>
            </div>

            {transferTargetId && (
              <div style={{
                background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)',
                borderRadius: '10px', padding: '12px 14px', marginBottom: '16px',
                color: '#fca5a5', fontSize: '13px', lineHeight: '1.6'
              }}>
                ⚠️ This action cannot be undone by you. Only the new owner can transfer it back.
              </div>
            )}

            <div className="modal-actions">
              <button
                className="btn-secondary"
                onClick={() => setShowTransferModal(false)}
                disabled={transferring}
              >
                Cancel
              </button>
              <button
                className="btn-primary"
                onClick={handleTransferOwnership}
                disabled={!transferTargetId || transferring}
                style={transferTargetId ? { background: 'linear-gradient(135deg, #d97706, #b45309)' } : {}}
              >
                {transferring ? 'Transferring...' : '👑 Transfer Ownership'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Upgrade Modal */}
      {showUpgradeModal && (
        <div className="modal-overlay" onClick={() => setShowUpgradeModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ textAlign: 'center', maxWidth: '440px', padding: '40px 32px' }}>
            <div style={{ 
              width: '72px', height: '72px', borderRadius: '50%',
              background: 'linear-gradient(135deg, rgba(139,92,246,0.2), rgba(251,191,36,0.15))',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              margin: '0 auto 20px', fontSize: '36px'
            }}>🌟</div>
            <h2 style={{ marginBottom: '8px', fontSize: '22px' }}>{t('upgradePlan') || 'Upgrade Your Plan'}</h2>
            <p style={{ 
              color: 'rgba(255,255,255,0.5)', fontSize: '14px', 
              lineHeight: '1.7', marginBottom: '20px' 
            }}>
              {upgradeMessage}
            </p>
            <div style={{
              background: 'rgba(139,92,246,0.08)', border: '1px solid rgba(139,92,246,0.2)',
              borderRadius: '12px', padding: '16px', marginBottom: '24px', textAlign: 'left'
            }}>
              <p style={{ color: '#a78bfa', fontSize: '13px', fontWeight: '600', marginBottom: '8px' }}>
                {t('upgradeUnlock') || 'Upgrade to unlock:'}
              </p>
              <p style={{ color: 'rgba(255,255,255,0.7)', fontSize: '13px', lineHeight: '1.8', margin: 0 }}>
                ✓ {t('upgradeFeature1')}<br/>
                ✓ {t('upgradeFeature2')}<br/>
                ✓ {t('upgradeFeature3')}<br/>
                ✓ {t('upgradeFeature4')}
              </p>
            </div>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
              <button 
                className="btn-secondary" 
                onClick={() => setShowUpgradeModal(false)}
                style={{ flex: 1 }}
              >
                {t('maybeLater') || 'Maybe Later'}
              </button>
              <button 
                className="btn-primary" 
                onClick={() => navigate('/pricing')}
                style={{ flex: 1 }}
              >
                🚀 {t('viewPlans') || 'View Plans'}
              </button>
            </div>
            <p style={{ color: 'rgba(255,255,255,0.3)', fontSize: '12px', marginTop: '16px' }}>
              {t('upgradeNote') || 'Plans start at $8/mo. Cancel anytime.'}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};
const StoryCard = ({ story }) => {
  const navigate = useNavigate();

  const isTimeCapsuleLocked = story.timeCapsule?.enabled && 
    new Date(story.timeCapsule.unlockDate) > new Date();

  const handleClick = () => {
    if (isTimeCapsuleLocked) return;
    navigate(`/story/${story._id}`);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatUnlockDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatDuration = (seconds) => {
    if (!seconds) return '';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const hasEnhanced = story.enhancedVersions?.polished || story.enhancedVersions?.literary;
  const hasSynthesized = story.synthesizedAudio?.literary?.key || story.synthesizedAudio?.polished?.key;

  return (
    <div className={`story-card ${isTimeCapsuleLocked ? 'story-card--locked' : ''}`} onClick={handleClick} style={isTimeCapsuleLocked ? { cursor: 'default', opacity: 0.75 } : {}}>
      <div className="story-header">
        <h3>{isTimeCapsuleLocked ? '🔒 ' : ''}{story.title}</h3>
        <div className="story-badges">
          {isTimeCapsuleLocked && (
            <span className="badge-capsule" style={{ background: 'rgba(251,191,36,0.15)', color: '#fbbf24', padding: '2px 8px', borderRadius: '6px', fontSize: '11px', fontWeight: '600' }}>⏰ Time Capsule</span>
          )}
          {!isTimeCapsuleLocked && story.timeCapsule?.enabled && (
            <span className="badge-capsule-unlocked" style={{ background: 'rgba(34,197,94,0.15)', color: '#4ade80', padding: '2px 8px', borderRadius: '6px', fontSize: '11px', fontWeight: '600' }}>🎉 Unlocked</span>
          )}
          {hasSynthesized && <span className="badge-voice">🔊 Voice</span>}
          {hasEnhanced && <span className="badge-enhanced">✨ Enhanced</span>}
          <span className={`status-badge status-${story.status}`}>
            {story.status}
          </span>
        </div>
      </div>
      
      {isTimeCapsuleLocked ? (
        <p className="story-preview" style={{ color: 'rgba(255,255,255,0.35)', fontStyle: 'italic' }}>
          🔒 This story is sealed until {formatUnlockDate(story.timeCapsule.unlockDate)}
        </p>
      ) : (
        <p className="story-preview">
          {story.rawTranscript?.substring(0, 150)}
          {story.rawTranscript?.length > 150 ? '...' : ''}
        </p>
      )}
      
      <div className="story-meta">
        <span>{formatDate(story.createdAt)}</span>
        {story.audioDuration > 0 && <span>{formatDuration(story.audioDuration)}</span>}
        <span>{story.storytellerType === 'tree' ? story.storytellerName : story.storytellerId?.displayName}</span>
      </div>
    </div>
  );
};

export default FamilyCircle;